import { JSONSchema7 } from 'json-schema';
import { UiSchema } from '@rjsf/utils';
import {
  CodingAgentConfiguration,
  CodingAgentTool,
  CodingAgentType,
  McpTransport,
  ToolControlMode,
  ConditionGroup,
  CreateWorkflowNode,
  DataExportArchiveConfiguration,
  DataExportWebsiteConfiguration,
  DataLoaderArchiveFileConfiguration,
  DataLoaderGitHubProjectConfiguration,
  DataLoaderLocalFileConfiguration,
  DataLoaderWorkflowExecutionConfiguration,
  DefaultValueColumnConfiguration,
  DropPropertyConfiguration,
  EmbeddingConfiguration,
  ExplodeConfiguration,
  FileManagement,
  GitExportConfiguration,
  GitHubWikiPageUploadConfiguration,
  GroupMergeConfiguration,
  HierarchicalPromptMode,
  JSONToMarkdownConfiguration,
  KeywordMatchingConfiguration,
  KeywordMatchingValidationConfiguration,
  LegacyLLMCountTokensConfiguration,
  LLMClassifyConfiguration,
  LLMClusterConfiguration,
  LLMCountTokensConfiguration,
  LLMExtractionConfiguration,
  LLMExtractionValidationConfiguration,
  LLMHierarchicalPromptConfiguration,
  LLMPromptConfiguration,
  LLMResearchConfiguration,
  LLMSummarizeConfiguration,
  MappingConfiguration,
  MergeConfiguration,
  ModelType,
  MultimodalGeminiPromptConfiguration,
  PlotClusteringConfiguration,
  Project,
  Prompt,
  PromptModel,
  PythonFunctionSummary,
  QueryFilterConfiguration,
  RegexFindConfiguration,
  RemoveCodeBlockTagsConfiguration,
  RemoveCodeCommentsConfiguration,
  RenameColumnConfiguration,
  ReplaceTextShortcutsConfiguration,
  Secret,
  SecretType,
  SimpleFilterConfiguration,
  SoftwareProjectExtractorSettings,
  SubworkflowCommonConfiguration,
  VisualizeDiagramConfiguration,
  WebScrapingConfiguration,
  WordParserConfiguration,
  Workflow,
  WorkflowNode,
  WorkflowNodeType,
} from 'api/model.ts';
import { ReactElement } from 'react';
import {
  FaWandMagicSparkles,
  FaLaptopFile,
  FaFileExport,
  FaFont,
  Fa42Group,
  FaDiagramNext,
  FaDiagramProject,
  FaLayerGroup,
  FaCodeMerge,
  FaEdge,
  FaTableCells,
  FaFilter,
  FaDice,
  FaKey,
  FaBoxArchive,
  FaRegChartBar,
  FaDeleteLeft,
  FaBuffer,
  FaCommentMedical,
  FaArrowRight,
  FaMagnifyingGlass,
  FaGlobe,
  FaArrowsUpDownLeftRight,
  FaPython,
  FaSitemap,
  FaGithub,
  FaFileZipper,
  FaArrowRotateLeft,
  FaArrowRightFromBracket,
  FaArrowRightToBracket,
  FaFileCode,
  FaEraser,
  FaGitAlt,
  FaRepeat,
  FaMicroscope,
  FaCommentSlash,
  FaArrowUp,
  FaArrowDown,
  FaPenToSquare,
  FaMarkdown,
  FaObjectGroup,
} from 'react-icons/fa6';
import { RiRobot2Fill } from 'react-icons/ri';
import { FaPlusSquare, FaRegFileAlt, FaRegCheckCircle } from 'react-icons/fa';

import { HiOutlineCode, HiOutlineDocumentText } from 'react-icons/hi';
import { FileManagementWidget } from 'components/form/FileManagementWidget';
import { innerDownloadLink } from 'api/file-management/requests';
import { HiddenConstantWidget } from 'components/form/HiddenConstantWidget';
import { ColumnFromFileWidget } from 'components/form/ColumnFromFileWidget';
import { SourceStepWidget } from 'components/form/SourceStepWidget.tsx';
import FieldWithUpload, {
  FieldWithUploadProps,
  validateArrayOfString,
  validateArrayOfStringMap,
} from 'components/form/FieldWithUpload';
import ValidationIcon from 'components/icon/ValidationIcon';
import {
  ARCHIVE_FILE_TYPES,
  CSV_FILE_TYPES,
  DOC_FILE_TYPES,
  EXCEL_FILE_TYPES,
  JSON_FILE_TYPES,
  RTF_FILE_TYPES,
  simplifiedMimeType,
} from 'utils/helpers.tsx';
import EmbedWorkflowNode from '../../views/flow/canvas/nodes/EmbedWorkflowNode.tsx';
import MergeNode from '../../views/flow/canvas/nodes/MergeNode.tsx';
import { CustomFormat } from 'components/form/CustomFormats.ts';
import ExtractionSchemaForm from '../../views/flow/form/ExtractionSchemaForm.tsx';
import SummarizeSchemaForm from '../../views/flow/form/SummarizeSchemaForm.tsx';
import ClassifySchemaForm from '../../views/flow/form/ClassifySchemaForm.tsx';
import EmbedWorkflowForm from '../../views/flow/form/EmbedWorkflowForm.tsx';
import MergeSchemaForm from '../../views/flow/form/MergeSchemaForm.tsx';
import FormBlock, { FormBlockProps } from '../../components/form/FormBlock.tsx';
import CodeEditorWidget from '../../components/form/CodeEditorWidget.tsx';
import PromptWidget from '../../components/form/PromptWidget.tsx';
import ProxyNode from '../../views/flow/canvas/nodes/ProxyNode.tsx';
import LLMNodeWithInputData from 'views/flow/canvas/nodes/LLMNodeWithInputData.tsx';
import QueryBuilderField from 'components/form/QueryBuilderField.tsx';
import McpServersField from 'components/form/McpServersField.tsx';
import { LOOP_EXIT_INITIAL_VALUE } from 'consts/codeEditorInitialValues.ts';
import PythonFunctionForm from 'views/flow/form/PythonFunctionForm.tsx';
import PythonFunctionWidget from '../../components/form/PythonFunctionWidget.tsx';
import PythonFunctionNode from 'views/flow/canvas/nodes/PythonFunctionNode.tsx';
import { REPORT_GENERATE_INITIAL_YAML } from './report_generate_initial_yaml';
import ArchiveFileForm, { EXTRACTOR_SETTINGS_SOFTWARE_PROJECT } from 'views/flow/form/ArchiveFileForm.tsx';
import PromptInputsField from 'components/form/PromptInputsField.tsx';
import ResizableTextarea from 'components/form/ResizableTextarea.tsx';
import ListSelectionWidget from 'components/form/ListSelectionWidget.tsx';
import MultiSelectWidget from 'components/form/MultiSelectWidget.tsx';
import PerplexityDateFormatWidget from 'components/form/PerplexityDateFormatWidget.tsx';
import ModelRenderer from 'components/form/CustomRenderers/ModelRenderer.tsx';
import LLMCommonForm from 'views/flow/form/LLMCommonForm.tsx';
import CodingAgentForm from 'views/flow/form/CodingAgentForm.tsx';
import ExecutionSelectWidget from 'components/form/ExecutionSelectWidget.tsx';
import WorkflowSelectWidget from 'components/form/WorkflowSelectWidget';
import ExecutionNodeSelectWidget from 'components/form/ExecutionNodeSelectWidget.tsx';
import { getLastCompletedWorkflowExecution, getNodeOutputData } from 'api/workflows/requests.ts';
import SecretWidget from 'components/form/SecretWidget.tsx';
import ToggleButtonWidget from 'components/form/ToggleButtonWidget.tsx';
import LoopWorkflow from 'views/flow/form/LoopWorkflow.tsx';
import LLMPromptForm from 'views/flow/form/LLMPromptForm.tsx';

type StepConfig = {
  nodeConfig: NodeConfig;
  dataConfig: DataConfig;
};

export type NodeConfig = {
  name: string;
  group: string;
  color: string;
  icon: ReactElement;
  source: boolean;
  target: boolean;
  multitarget: boolean;
  minInputs: number;
  maxInstances?: number;
  definesExpectedColumns?: boolean;
};

export type WorkflowNodeConfigWithKey = NodeConfig & { nodeKey: WorkflowNodeType };

export type NodeFormProps = FormBlockProps<CreateWorkflowNode | WorkflowNode> & {
  nodeId: string;
};

type DataConfig = {
  getColumnsInfo: (data: WorkflowNode, embeddedWorkflows?: Workflow[]) => Promise<NodeColumnsInfo> | NodeColumnsInfo;
  jsonSchema: JSONSchema7;
  uiSchema?: UiSchema;
  customForm?: React.ComponentType<NodeFormProps>;
};

export type NodeColumnsInfo = {
  id: string;
  inputStepName: string;
  stepType?: WorkflowNodeType;
  fileId?: string;
  fileName?: string;
  inputColumns: string[];
  outputColumns: string[];
  removedColumns: string[];
  replacesDataframe: boolean;
};

const INDEX_COLUMN = 'giq_index';

// Category color palettes for consistent node styling
type CategoryColors = {
  data_loader: string;
  parsers: string;
  llm: string;
  filters: string;
  transformers: string;
  text_processing: string;
  flow_control: string;
  validation: string;
  exporters: string;
  git: string;
  advanced: string;
  legacy: string;
};

const CATEGORY_COLORS: CategoryColors = {
  data_loader: '#4C60C9',
  parsers: '#9B6DE0',
  llm: '#D0F440',
  filters: '#E86FA6',
  transformers: '#E0A32E',
  text_processing: '#22B8A6',
  flow_control: '#5B93F0',
  validation: '#1EC260',
  exporters: '#F0793E',
  git: '#3D4A70',
  advanced: '#E4002B',
  legacy: '#6B7A99',
};

const getCategoryColors = (): CategoryColors => CATEGORY_COLORS;

const getNodeColorByGroup = (group: string): string => {
  return CATEGORY_COLORS[group as keyof CategoryColors] ?? CATEGORY_COLORS.legacy;
};

type StepConfigArgs = {
  prompts?: Prompt[];
  promptsModels?: PromptModel[];
  files?: FileManagement[];
  columns?: NodeColumnsInfo[];
  workflows?: Workflow[];
  workflowId?: string;
  secrets?: Secret[];
  functions?: PythonFunctionSummary[];
};

const steps = new Map<WorkflowNodeType, (args?: StepConfigArgs) => StepConfig>();

/* *** steps - data loaders *** */
steps.set('data_loader_local_file', (args = {}): StepConfig => {
  const csvFiles = getFilesOptions(args.files, CSV_FILE_TYPES);
  const xlsFiles = getFilesOptions(args.files, EXCEL_FILE_TYPES);
  const docFiles = getFilesOptions(args.files, DOC_FILE_TYPES);
  const rtfFiles = getFilesOptions(args.files, RTF_FILE_TYPES);
  const filesOptions = [...csvFiles, ...xlsFiles, ...docFiles, ...rtfFiles];

  const fileTypeDependencies = [];

  if (csvFiles.length > 0) {
    fileTypeDependencies.push({
      properties: {
        file_path: {
          anyOf: csvFiles,
        },
        delimiter: { type: ['string', 'null'], title: 'Delimiter' },
        file_type: { type: 'string', const: 'csv' },
      },
    });
  }

  if (xlsFiles.length > 0) {
    fileTypeDependencies.push({
      properties: {
        file_path: {
          anyOf: xlsFiles,
        },
        file_type: { type: 'string', const: 'xls' },
      },
    });
  }

  if (docFiles.length > 0) {
    fileTypeDependencies.push({
      properties: {
        file_path: {
          anyOf: docFiles,
        },
        file_type: { type: 'string', const: 'docx' },
      },
    });
  }

  if (rtfFiles.length > 0) {
    fileTypeDependencies.push({
      properties: {
        file_path: {
          anyOf: rtfFiles,
        },
      },
      file_type: { type: 'string', const: 'rtf' },
    });
  }

  return {
    nodeConfig: {
      name: 'Local file',
      group: 'data_loader',
      color: CATEGORY_COLORS.data_loader,
      icon: <FaLaptopFile />,
      source: true,
      target: false,
      multitarget: false,
      minInputs: 0,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as DataLoaderLocalFileConfiguration;
        const file = args.files?.find((f) => innerDownloadLink(f.id) == cfg.file_path);

        if (file?.meta?.content) {
          return {
            id: data.id,
            inputStepName: data.name,
            fileId: file.id,
            fileName: file.file_name,
            inputColumns: [],
            outputColumns: [
              ...file.meta.content.flatMap((m) => m.columns ?? []).filter((column) => column),
              INDEX_COLUMN,
            ],
            removedColumns: [],
            replacesDataframe: false,
          };
        }
        return emptyColumnsInfo(data);
      },
      jsonSchema: {
        title: 'Local file loader',
        type: 'object',
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            type: 'object',
            title: 'Configuration',
            properties: {
              file_path: {
                type: 'string',
                title: 'File',
                anyOf: filesOptions,
              },
            },
            required: ['file_path'],
            dependencies: {
              file_path: {
                oneOf: [...fileTypeDependencies],
              },
            },
          },
        },
        required: ['name'],
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          file_path: {
            'ui:widget': FileManagementWidget,
            'ui:options': {
              accept: '.xls,.xlsx,.csv,.docx,.rtf,',
              placeholder: 'Select a file',
              enumOptions: filesOptions,
            },
          },
          file_type: {
            'ui:widget': HiddenConstantWidget,
            'ui:style': { marginTop: '-1.5rem' },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('data_loader_workflow_execution', (args = {}): StepConfig => {
  const workflows = args.workflows;
  const currentWorkflowId = args.workflowId;
  const workflowsOptions = getWorkflowsOptions(workflows).filter((workflow) => workflow.const !== currentWorkflowId);

  return {
    nodeConfig: {
      name: 'Workflow Execution',
      group: 'data_loader',
      color: CATEGORY_COLORS.data_loader,
      icon: <FaRegCheckCircle />,
      source: true,
      target: false,
      multitarget: false,
      minInputs: 0,
    },
    dataConfig: {
      getColumnsInfo: async (data: WorkflowNode) => {
        const cfg = data.configuration as DataLoaderWorkflowExecutionConfiguration;

        let executionId = cfg.execution_id;
        const workflowId = cfg.workflow_id;
        const nodeId = cfg.node_id;

        if (!workflowId) {
          return emptyColumnsInfo(data);
        }

        const workflow = workflows?.find((w) => w.id === workflowId);
        if (!workflow) {
          return emptyColumnsInfo(data);
        }

        if (executionId === '') {
          const execution = await getLastCompletedWorkflowExecution(workflow.project_id ?? '', workflowId ?? '');
          executionId = execution?.id ?? '';
        }

        if (!executionId) {
          return emptyColumnsInfo(data);
        }

        const result = await getNodeOutputData({
          workflowExecutionId: executionId,
          nodeId: nodeId,
          pageParam: 1,
          pageSize: 1,
          sortColumn: INDEX_COLUMN,
          sortDirection: '',
        });
        if (result.data.length === 0) {
          return emptyColumnsInfo(data);
        }
        const columns = Object.keys(result.data[0]);

        return {
          id: data.id,
          inputStepName: data.name,
          fileName: `${workflow.name} [${workflow.revision}]`,
          inputColumns: [],
          outputColumns: columns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Workflow Execution loader',
        type: 'object',
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            type: 'object',
            title: 'Configuration',
            properties: {
              workflow_id: {
                type: 'string',
                title: 'Workflow',
                anyOf: workflowsOptions,
              },
              execution_id: {
                type: 'string',
                title: 'Execution',
                default: '',
              },
              node_id: {
                type: 'string',
                title: 'Node',
              },
            },
            required: ['workflow_id', 'node_id'],
          },
        },
        required: ['name'],
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          workflow_id: {
            'ui:widget': WorkflowSelectWidget,
            'ui:options': {
              workflows,
              currentWorkflowId,
            },
          },
          execution_id: {
            'ui:widget': ExecutionSelectWidget,
            'ui:options': {
              label: 'Execution',
            },
          },
          node_id: {
            'ui:widget': ExecutionNodeSelectWidget,
            'ui:options': {
              label: 'Node',
            },
          },
        },
      },
    },
  } as StepConfig;
});

const softwareProjectExtractorSettings: JSONSchema7 = {
  type: 'object',
  title: 'Columns settings',
  properties: {
    include_text_content: {
      type: 'boolean',
      title: 'Include text content?',
      description: 'If enabled, the decoded text content of the files will be included (text_content column)',
      default: true,
    },
    include_binary_content: {
      type: 'boolean',
      title: 'Include binary content?',
      description: 'If enabled, the binary content of the files will be included in base64 (binary_content column)',
      default: false,
    },
  },
};

steps.set('data_loader_archive_file', (args = {}): StepConfig => {
  const extractorsOptions: SelectOption[] = [
    {
      const: 'tab_data_extractor',
      title: 'Tabular data extractor',
    },
    {
      const: 'software_project_extractor',
      title: 'Software project extractor',
    },
    {
      const: 'media_extractor',
      title: 'Media extractor (pdf, image, audio, video)',
    },
  ];

  return {
    nodeConfig: {
      name: 'Archive file',
      group: 'data_loader',
      color: CATEGORY_COLORS.data_loader,
      icon: <FaBoxArchive />,
      source: true,
      target: false,
      multitarget: false,
      minInputs: 0,
      definesExpectedColumns: true,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as DataLoaderArchiveFileConfiguration;
        const baseInfo = {
          id: data.id,
          inputStepName: data.name,
          inputColumns: [],
          removedColumns: [],
          replacesDataframe: false,
        };
        if (cfg.extractor_type == 'software_project_extractor') {
          const columns = [INDEX_COLUMN, 'file_name', 'path', 'unique_file_name', 'extension', 'mime_type'];
          const extractorSettings = cfg.extractor_settings as SoftwareProjectExtractorSettings;
          if (extractorSettings?.include_text_content) {
            columns.push('text_content');
          }
          if (extractorSettings?.include_binary_content) {
            columns.push('binary_content');
          }

          return {
            ...baseInfo,
            outputColumns: columns,
          };
        } else if (cfg.extractor_type == 'media_extractor') {
          return {
            ...baseInfo,
            outputColumns: [
              INDEX_COLUMN,
              'file_name',
              'path',
              'unique_file_name',
              'extension',
              'mime_type',
              'binary_content',
            ],
          };
        }
        return {
          ...baseInfo,
          outputColumns: [INDEX_COLUMN],
        };
      },
      customForm: ArchiveFileForm,
      jsonSchema: {
        title: 'Archive file loader',
        type: 'object',
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            type: 'object',
            title: 'Configuration',
            properties: {
              file_path: {
                type: 'string',
                title: 'File',
                anyOf: getFilesOptions(args.files, ARCHIVE_FILE_TYPES),
              },
              extractor_type: {
                type: 'string',
                title: 'Extractor type',
                anyOf: extractorsOptions,
              },
            },
            dependencies: {
              extractor_type: {
                oneOf: [
                  {
                    properties: {
                      extractor_type: {
                        const: 'tab_data_extractor',
                      },
                      extractor_settings: {
                        type: 'object',
                        title: 'Extractor settings',
                        properties: {
                          separator: {
                            type: 'string',
                            title: 'Separator',
                            description: 'Column separator for the tabular data',
                          },
                        },
                      },
                    },
                  },
                  {
                    properties: {
                      extractor_type: {
                        const: 'media_extractor',
                      },
                    },
                  },
                  {
                    properties: {
                      extractor_type: {
                        const: 'software_project_extractor',
                      },
                      extractor_settings: softwareProjectExtractorSettings,
                    },
                  },
                ],
              },
            },
            required: ['file_path', 'extractor_type'],
          },
        },
        required: ['name'],
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          file_path: {
            'ui:widget': FileManagementWidget,
            'ui:options': {
              accept: '.zip',
            },
          },
          file_type: {
            'ui:widget': HiddenConstantWidget,
            'ui:style': { marginTop: '-1.5rem' },
          },
          extractor_type: {
            'ui:widget': ListSelectionWidget,
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('data_loader_github_project', (): StepConfig => {
  return {
    nodeConfig: {
      name: 'Git Project',
      group: 'data_loader',
      color: CATEGORY_COLORS.data_loader,
      icon: <FaGitAlt />,
      source: true,
      target: false,
      multitarget: false,
      minInputs: 0,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as DataLoaderGitHubProjectConfiguration;
        const columns = [INDEX_COLUMN, 'file_name', 'path', 'unique_file_name', 'extension', 'mime_type'];
        if (cfg.extractor_settings?.include_text_content) {
          columns.push('text_content');
        }
        if (cfg.extractor_settings?.include_binary_content) {
          columns.push('binary_content');
        }

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns: [],
          outputColumns: columns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Git project loader',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            properties: {
              repo_url: {
                type: 'string',
                title: 'Repository URL',
              },
              branch: {
                type: ['string', 'null'],
                title: 'Branch',
                description: 'Defaults to the default branch of the repository',
              },
              subdirectory: {
                type: ['string', 'null'],
                title: 'Subdirectory',
                format: CustomFormat.Subdirectory,
                description: 'Path to a subdirectory in the repository to load files from e.g. "src/data" (optional)',
              },
              secret_id: {
                type: ['string', 'null'],
                title: 'Authentication Secret',
                description: 'Authentication secret for private repositories e.g. access token or username:password',
              },
              extractor_settings: softwareProjectExtractorSettings,
            },
            required: ['repo_url'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          extractor_settings: EXTRACTOR_SETTINGS_SOFTWARE_PROJECT as UiSchema,
          secret_id: {
            'ui:widget': SecretWidget,
            'ui:options': {
              types: ['git_auth'],
            },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('data_loader_llm_research', (args = {}): StepConfig => {
  const inputColumns = args.columns;
  const prompts = args.prompts ?? [];
  const models = args.promptsModels ?? [];
  const revisionsOptions = getPromptsRevisionsOptions(prompts, true);
  const maxRevision = Math.max(...(prompts ?? []).map((prompt) => prompt.revision));
  return {
    nodeConfig: {
      name: 'LLM Research',
      group: 'data_loader',
      color: CATEGORY_COLORS.data_loader,
      icon: <FaMicroscope />,
      source: true,
      target: false,
      multitarget: false,
      minInputs: 0,
    },
    dataConfig: getLLMResearchDataConfig(models, revisionsOptions, maxRevision, inputColumns, false),
  } as StepConfig;
});

/* *** steps - LLM *** */

const MULTIMODAL_FILE_TYPES = [
  'application/pdf',
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/ogg',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/bmp',
  'text/plain',
  'video/mov',
  'video/mpeg',
  'video/mp4',
  'video/mpg',
  'video/avi',
  'video/flv',
];

steps.set('llm_prompt', (args = {}): StepConfig => {
  const prompts = args.prompts ?? [];
  const promptsModelsOptions = getPromptsModelsOptions(args.promptsModels);
  const revisionsOptions = getPromptsRevisionsOptions(prompts, true);
  const evaluationRevisionsOptions = getEvaluationPromptsRevisionsOptions(prompts);
  const maxRevision = Math.max(...(prompts ?? []).map((prompt) => prompt.revision));

  return {
    nodeConfig: {
      name: 'Prompt',
      group: 'llm',
      color: CATEGORY_COLORS.llm,
      icon: <FaFont />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as LLMPromptConfiguration;
        const inputColumns: string[] = [];
        if (cfg.input_data) {
          for (const key in cfg.input_data) {
            const columnName = cfg.input_data[key];
            if (columnName && columnName.trim() !== '') {
              inputColumns.push(columnName);
            }
          }
        }
        if (cfg.multimodal_file_content_column) {
          inputColumns.push(cfg.multimodal_file_content_column);
        }
        if (cfg.multimodal_file_type_column) {
          inputColumns.push(cfg.multimodal_file_type_column);
        }
        const outputColumns = cfg.output_column ? [cfg.output_column] : [];
        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      customForm: LLMPromptForm,
      jsonSchema: {
        title: 'LLM Prompt',
        type: 'object',
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            properties: {
              model_id: {
                type: 'string',
                title: 'Prompts model',
                anyOf: promptsModelsOptions,
              },
              temperature: {
                type: ['number', 'null'],
                title: 'Temperature',
                default: 0,
              },
              output_column: {
                type: 'string',
                title: 'Output column name',
                format: CustomFormat.ColumnName,
              },
              threads: {
                type: 'integer',
                title: 'Threads',
                description: 'Number of parallel threads to use for processing the rows.',
                default: 1,
                minimum: 1,
                maximum: 10,
              },
              fallback_model_id: {
                type: ['string', 'null'],
                title: 'Fallback model',
                anyOf: [{ const: null }, ...promptsModelsOptions],
              },
              fallback_token_threshold: {},
              prompt_id: {
                type: 'string',
                title: 'Prompt',
                anyOf: prompts.map((prompt) => ({ const: prompt.id, title: prompt.prompt_name })),
              },
              prompt_revision: {
                type: 'number',
                title: 'Prompt revision',
                anyOf: Array.from({ length: maxRevision }, (_, i) => ({ const: i + 1 })).reverse(),
                readOnly: true,
              },
              input_data: {
                type: 'object',
              },

              expand_multimodal_settings: {
                type: 'boolean',
                title: 'Multimodal',
                description: 'Show additional settings for multimodal prompts.',
                default: false,
              },
              expand_evaluation_settings: {
                type: 'boolean',
                title: 'Evaluation',
                description: 'Show additional settings for prompt results evaluation with retries.',
                default: false,
              },
              log_prompts: {
                type: 'boolean',
                title: 'Log prompts',
                description: 'If enabled, the generated prompts will be logged for each row.',
                default: false,
              },
            },
            dependencies: {
              fallback_model_id: {
                oneOf: [
                  {
                    properties: {
                      fallback_model_id: {
                        type: 'null',
                      },
                    },
                  },
                  {
                    properties: {
                      fallback_model_id: {
                        anyOf: promptsModelsOptions,
                      },
                      fallback_token_threshold: {
                        type: 'number',
                        title: 'Fallback token threshold',
                        description:
                          'If the number of tokens in the prompt exceeds this value, the fallback model will be used.',
                      },
                    },
                    required: ['fallback_token_threshold'],
                  },
                ],
              },
              prompt_id: {
                oneOf: revisionsOptions,
              },
              evaluation_prompt_id: {
                oneOf: evaluationRevisionsOptions,
              },
              expand_multimodal_settings: {
                oneOf: [
                  {
                    properties: {
                      expand_multimodal_settings: {
                        const: false,
                      },
                    },
                  },
                  {
                    properties: {
                      expand_multimodal_settings: {
                        const: true,
                      },
                      multimodal_file_content_column: {
                        type: ['string', 'null'],
                        title: 'Multimodal file content column',
                        description: 'Column containing the file content (base64 encoded) for multimodal prompts.',
                        format: CustomFormat.ColumnName,
                      },
                      multimodal_file_type_column: {
                        type: ['string', 'null'],
                        title: 'Multimodal file type column',
                        description: 'Column containing the file mime type for multimodal prompts.',
                        format: CustomFormat.ColumnName,
                      },
                      multimodal_file_type: {
                        type: ['string', 'null'],
                        title: 'Multimodal file type',
                        enum: [null, ...MULTIMODAL_FILE_TYPES],
                        description: 'Fixed mime type for multimodal prompt files (or fallback to mime type column).',
                      },
                    },
                  },
                ],
              },
              expand_evaluation_settings: {
                oneOf: [
                  {
                    properties: {
                      expand_evaluation_settings: {
                        const: false,
                      },
                    },
                  },
                  {
                    properties: {
                      expand_evaluation_settings: {
                        const: true,
                      },
                      evaluation_model_id: {
                        type: 'string',
                        title: 'Evaluation model',
                        anyOf: promptsModelsOptions,
                      },
                      evaluation_prompt_id: {
                        type: 'string',
                        title: 'Evaluation prompt',
                        anyOf: prompts.map((prompt) => ({ const: prompt.id, title: prompt.prompt_name })),
                        description: 'The prompt must have the following inputs: {prompt}, {response}.',
                      },
                      evaluation_prompt_revision: {
                        type: 'number',
                        title: 'Evaluation prompt revision',
                        anyOf: Array.from({ length: maxRevision }, (_, i) => ({ const: i + 1 })).reverse(),
                        readOnly: true,
                      },
                      evaluation_operator: {
                        type: 'string',
                        title: 'Evaluation operator',
                        enum: [
                          '=',
                          '!=',
                          'CONTAINS',
                          'NOT CONTAINS',
                          'STARTS WITH',
                          'ENDS WITH',
                          'CONTAINS WORD',
                          'NOT CONTAINS WORD',
                          'STARTS WITH WORD',
                          'ENDS WITH WORD',
                        ],
                        default: '=',
                        description:
                          'The operator to compare the evaluation prompt output with the expected value. The comparison is case insensitive.',
                      },
                      evaluation_expected_value: {
                        type: 'string',
                        title: 'Evaluation expected value',
                        description: 'The expected value to compare the evaluation prompt output with.',
                      },
                      evaluation_max_retries: {
                        type: 'number',
                        title: 'Max retries',
                        description: 'Number of retries for the evaluation prompt in case of failure.',
                        default: 2,
                      },
                    },
                    required: [
                      'evaluation_model_id',
                      'evaluation_prompt_id',
                      'evaluation_prompt_revision',
                      'evaluation_operator',
                      'evaluation_expected_value',
                      'evaluation_max_retries',
                    ],
                  },
                ],
              },
            },
            required: ['model_id', 'prompt_id', 'prompt_revision', 'output_column'],
          },
        },
        required: ['name'],
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          'ui:order': [
            '*',
            'expand_multimodal_settings',
            'multimodal_file_content_column',
            'multimodal_file_type_column',
            'multimodal_file_type',
            'expand_evaluation_settings',
            'evaluation_model_id',
            'evaluation_prompt_id',
            'evaluation_prompt_revision',
            'evaluation_operator',
            'evaluation_expected_value',
            'evaluation_max_retries',
            'log_prompts',
          ],
          input_data: {
            'ui:field': PromptInputsField,
            'ui:options': { prompts, columns: args.columns },
          },
          prompt_id: {
            'ui:widget': PromptWidget,
          },
          model_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': {
              autocomplete: true,
              render: (modelId: string) => <ModelRenderer modelId={modelId} models={args.promptsModels!} />,
            },
          },
          fallback_model_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': {
              autocomplete: true,
              render: (modelId: string) => <ModelRenderer modelId={modelId} models={args.promptsModels!} />,
            },
          },
          evaluation_model_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': {
              autocomplete: true,
              render: (modelId: string) => <ModelRenderer modelId={modelId} models={args.promptsModels!} />,
            },
          },
          evaluation_prompt_id: {
            'ui:widget': PromptWidget,
            'ui:options': {
              requiredInputs: ['prompt', 'response'],
              additionalInputsAllowed: false,
              templateHint:
                'The {prompt} input will be populated with the original prompt and the {response} input will be populated with the generated response for evaluation. The evaluation prompt should output a value that can be compared to the expected value using the selected operator.',
            },
          },
          multimodal_file_type_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          multimodal_file_content_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          multimodal_file_type: {
            'ui:widget': ListSelectionWidget,
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('multimodal_gemini_prompt', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Multimodal prompt',
      group: 'legacy',
      color: CATEGORY_COLORS.legacy,
      icon: <FaEdge />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as MultimodalGeminiPromptConfiguration;
        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns: cfg.column_name ? [cfg.column_name] : [],
          outputColumns: cfg.result_column ? [cfg.result_column] : [],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Multimodal Gemini Prompt',
        type: 'object',
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            type: 'object',
            title: 'Configuration',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
              result_column: {
                type: 'string',
                title: 'Result column name',
                format: CustomFormat.ColumnName,
              },
              project_id: {
                type: 'string',
                title: 'GCP Project ID',
              },
              model_name: {
                type: 'string',
                title: 'Model name',
                enum: [
                  'gemini-1.5-flash-002',
                  'gemini-1.5-pro-002',
                  'gemini-2.0-flash-exp',
                  'gemini-2.0-pro-exp',
                  'gemini-1.5-flash-001',
                  'gemini-1.5-pro-001',
                ],
              },
              mime_type: {
                type: 'string',
                title: 'Mime type',
                enum: [
                  'application/pdf',
                  'audio/mpeg',
                  'audio/mp3',
                  'audio/wav',
                  'image/png',
                  'image/jpeg',
                  'text/plain',
                  'video/mov',
                  'video/mpeg',
                  'video/mp4',
                  'video/mpg',
                  'video/avi',
                  'video/wmv',
                  'video/mpegps',
                  'video/flv',
                ],
              },
              location: {
                type: 'string',
                title: 'Location',
                enum: ['us-central1', 'us-west4', 'europe-west3', 'europe-central2'],
              },
              prompt: {
                type: 'string',
              },
            },
            required: ['column_name', 'result_column', 'project_id'],
          },
        },
        required: ['name'],
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
          prompt: {
            'ui:widget': ResizableTextarea,
            'ui:options': {
              rows: 4,
              style: {
                resize: 'vertical',
              },
            },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('llm_extraction', (args = {}): StepConfig => {
  const prompts = args.prompts ?? [];
  const promptsModelsOptions = getPromptsModelsOptions(args.promptsModels);
  const revisionsOptions = getPromptsRevisionsOptions(prompts);
  const maxRevision = Math.max(...(prompts ?? []).map((prompt) => prompt.revision));

  return {
    nodeConfig: {
      name: 'Extraction',
      group: 'llm',
      color: CATEGORY_COLORS.llm,
      icon: <FaWandMagicSparkles />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as LLMExtractionConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];
        const outputColumns = cfg.extraction_schema.properties ? Object.keys(cfg.extraction_schema.properties) : [];
        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      customForm: ExtractionSchemaForm,
      jsonSchema: {
        title: 'LLM Extraction',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            $ref: '#/definitions/LLMExtractionConfiguration',
          },
        },
        definitions: {
          LLMExtractionConfiguration: {
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
              model_id: {
                type: 'string',
                title: 'Prompts model',
                anyOf: promptsModelsOptions,
              },
              prompt_id: {
                type: ['string', 'null'],
                title: 'Prompt',
              },
              prompt_revision: {
                type: 'number',
                title: 'Prompt revision',
                anyOf: Array.from({ length: maxRevision }, (_, i) => ({ const: i + 1 })).reverse(),
                readOnly: true,
              },
              temperature: {
                type: ['number', 'null'],
                title: 'Temperature',
                default: 0,
              },
              log_prompts: {
                type: 'boolean',
                title: 'Log prompts',
                description: 'If enabled, the generated prompts will be logged for each row.',
                default: false,
              },
              // extraction_schema - see schemaLLMExtractionSchemaProperty,
            },
            required: ['column_name', 'model_id', 'prompt_id', 'prompt_revision'],
            dependencies: {
              prompt_id: {
                oneOf: revisionsOptions,
              },
            },
            title: 'LLMExtractionConfiguration',
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          'ui:order': ['*', 'log_prompts'],
          column_name: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
          prompt_id: {
            'ui:widget': PromptWidget,
          },
          model_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': {
              autocomplete: true,
              render: (modelId: string) => <ModelRenderer modelId={modelId} models={args.promptsModels!} />,
            },
          },
        },
      },
    },
  } as StepConfig;
});

// it is used to hack llm_extraction extraction schema fields
// see LLMExtractionSchemaPropertyTemp model and ExtractionSchemaForm component
const schemaLLMExtractionSchemaProperty: JSONSchema7 = {
  type: 'object',
  properties: {
    field_key: {
      type: 'string',
      title: 'Field key (name)',
    },
    type: {
      type: 'string',
      title: 'Field type',
      enum: ['string', 'boolean', 'number', 'array'],
      default: 'string',
    },
    description: {
      title: 'Field description',
      type: 'string',
    },
    required: {
      type: 'boolean',
      title: 'Is required?',
      default: false,
    },
  },
  required: ['field_key', 'type', 'description'],
  dependencies: {
    type: {
      oneOf: [
        {
          properties: {
            type: {
              enum: ['string', 'number'],
            },
            is_enum: {
              type: 'boolean',
              title: 'Only predefined values (is enum)?',
              default: false,
            },
          },
        },
        {
          properties: {
            type: {
              enum: ['array'],
            },
            array_items_type: {
              type: 'string',
              title: 'Array items type',
              enum: ['string', 'boolean', 'number'],
            },
          },
          required: ['array_items_type'],
        },
        {
          properties: {
            type: {
              enum: ['boolean'],
            },
          },
        },
      ],
    },
    is_enum: {
      oneOf: [
        {
          properties: {
            is_enum: {
              const: true,
            },
            enum: {
              type: 'array',
              title: 'Possible values:',
              items: {
                type: 'string',
              },
            },
          },
        },
        {
          properties: {
            is_enum: {
              const: false,
            },
          },
        },
      ],
    },
  },
};

const uiSchemaLLMExtractionSchemaProperty: UiSchema = {
  is_enum: {
    'ui:style': {
      marginTop: '-2rem',
    },
  },
  enum: {
    'ui:field': (props: FieldWithUploadProps) => (
      <FieldWithUpload {...props} validate={validateArrayOfString} buttonText="Upload values" fieldType="array" />
    ),
    'ui:options': {
      orderable: false,
    },
  },
};

steps.set('llm_count_tokens', (args = {}): StepConfig => {
  const promptsModelsOptions = getPromptsModelsOptions(args.promptsModels);
  return {
    nodeConfig: {
      name: 'Count tokens',
      group: 'llm',
      color: CATEGORY_COLORS.llm,
      icon: <FaCommentMedical />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as LLMCountTokensConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];
        const outputColumns = cfg.column_name ? [`${cfg.column_name}_token_count`] : [];
        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Count tokens',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
              model_id: {
                type: 'string',
                title: 'Model',
                anyOf: promptsModelsOptions,
              },
              threads: {
                type: 'integer',
                title: 'Threads',
                description: 'Number of parallel threads to use for processing the rows.',
                default: 1,
                minimum: 1,
                maximum: 10,
              },
            },
            required: ['column_name', 'model_id'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          model_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': {
              autocomplete: true,
              render: (modelId: string) => <ModelRenderer modelId={modelId} models={args.promptsModels!} />,
            },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('legacy_llm_count_tokens', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Legacy Count tokens',
      group: 'legacy',
      color: CATEGORY_COLORS.legacy,
      icon: <FaCommentMedical />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as LegacyLLMCountTokensConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];
        const outputColumns = cfg.column_name ? [`${cfg.column_name}_token_count`] : [];
        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Legacy Count tokens',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
              strategy: {
                type: 'string',
                title: 'Strategy',
                enum: ['approximation', 'openai', 'vertex', 'anthropic'],
                default: 'approximation',
              },
            },
            required: ['column_name'],
            allOf: [
              {
                if: {
                  properties: { strategy: { const: 'vertex' } },
                },
                then: {
                  properties: {
                    model_name: {
                      title: 'Model name',
                      type: 'string',
                      enum: ['gemini-1.5-pro-001', 'gemini-1.5-flash-001'],
                    },
                    location: {
                      type: 'string',
                      title: 'Location',
                      enum: ['us-central1', 'us-west4', 'europe-west3', 'europe-central2'],
                    },
                    project_id: { type: 'string' },
                  },
                },
              },
              {
                if: {
                  properties: { strategy: { const: 'anthropic' } },
                },
                then: {
                  properties: {
                    anthropic_model: {
                      title: 'Anthropic Model',
                      type: 'string',
                      enum: ['claude-3-opus-latest', 'claude-3-5-sonnet-latest', 'claude-3-5-haiku-latest'],
                    },
                  },
                  required: ['anthropic_model'],
                },
              },
            ],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('llm_classify', (args = {}): StepConfig => {
  const prompts = args.prompts ?? [];
  const promptsModelsOptions = getPromptsModelsOptions(args.promptsModels);
  const revisionsOptions = getPromptsRevisionsOptions(prompts);
  const maxRevision = Math.max(...(prompts ?? []).map((prompt) => prompt.revision));
  return {
    nodeConfig: {
      name: 'Classify',
      group: 'llm',
      color: CATEGORY_COLORS.llm,
      icon: <Fa42Group />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as LLMClassifyConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];
        const outputColumns = cfg.classify_schema.properties ? Object.keys(cfg.classify_schema.properties) : [];
        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      customForm: ClassifySchemaForm,
      jsonSchema: {
        title: 'LLM Classify',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            $ref: '#/definitions/LLMClassifyConfiguration',
          },
        },
        definitions: {
          LLMClassifyConfiguration: {
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
              model_id: {
                type: 'string',
                title: 'Prompts model',
                anyOf: promptsModelsOptions,
              },
              prompt_id: {
                type: ['string', 'null'],
                title: 'Prompt',
              },
              prompt_revision: {
                type: 'number',
                title: 'Prompt revision',
                anyOf: Array.from({ length: maxRevision }, (_, i) => ({ const: i + 1 })).reverse(),
                readOnly: true,
              },
              temperature: {
                type: ['number', 'null'],
                title: 'Temperature',
                default: 0,
              },
              log_prompts: {
                type: 'boolean',
                title: 'Log prompts',
                description: 'If enabled, the generated prompts will be logged for each row.',
                default: false,
              },
            },
            required: ['column_name', 'model_id', 'prompt_id', 'prompt_revision'],
            dependencies: {
              prompt_id: {
                oneOf: revisionsOptions,
              },
            },
            title: 'LLMClassifyConfiguration',
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          'ui:order': ['*', 'log_prompts'],
          column_name: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
          prompt_id: {
            'ui:widget': PromptWidget,
          },
          model_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': {
              autocomplete: true,
              render: (modelId: string) => <ModelRenderer modelId={modelId} models={args.promptsModels!} />,
            },
          },
        },
      },
    },
  } as StepConfig;
});

const schemaLLMClassifySchemaProperty: JSONSchema7 = {
  type: 'object',
  properties: {
    field_key: {
      type: 'string',
      title: 'Field key (name)',
    },
    type: {
      type: 'string',
      title: 'Field type',
      enum: ['string'], // only string for now
      default: 'string',
    },
    description: {
      title: 'Field description',
      type: 'string',
    },
    classes: {
      type: 'array',
      title: 'Classes',
      items: {
        type: 'object',
        properties: {
          name: {
            type: 'string',
            title: 'Class Name',
          },
          description: {
            type: 'string',
            title: 'Class description',
          },
        },
        required: ['name', 'description'],
      },
      minItems: 2,
    },
  },
  required: ['field_key', 'type', 'description', 'classes'],
};

const uiSchemaLLMClassifySchemaProperty: UiSchema = {
  classes: {
    'ui:field': (props: FieldWithUploadProps) => (
      <FieldWithUpload
        {...props}
        validate={(data) => validateArrayOfStringMap(data, 2)}
        buttonText="Upload classes"
        fieldType="array"
      />
    ),
    'ui:options': {
      orderable: false,
    },
  },
};

steps.set('llm_summarize', (args = {}): StepConfig => {
  const prompts = args.prompts ?? [];
  const promptsModelsOptions = getPromptsModelsOptions(args.promptsModels);
  const revisionsOptions = getPromptsRevisionsOptions(prompts);
  const maxRevision = Math.max(...(prompts ?? []).map((prompt) => prompt.revision));
  return {
    nodeConfig: {
      name: 'Summarize',
      group: 'llm',
      color: CATEGORY_COLORS.llm,
      icon: <FaDiagramNext />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as LLMSummarizeConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];
        const outputColumns = cfg.summarize_schema.properties ? Object.keys(cfg.summarize_schema.properties) : [];
        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      customForm: SummarizeSchemaForm,
      jsonSchema: {
        title: 'LLM Summarize',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            $ref: '#/definitions/LLMSummarizeConfiguration', // configuration in provided below
          },
        },
        definitions: {
          LLMSummarizeConfiguration: {
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
              model_id: {
                type: 'string',
                title: 'Prompts model',
                anyOf: promptsModelsOptions,
              },
              prompt_id: {
                type: ['string', 'null'],
                title: 'Prompt',
              },
              prompt_revision: {
                type: 'number',
                title: 'Prompt revision',
                anyOf: Array.from({ length: maxRevision }, (_, i) => ({ const: i + 1 })).reverse(),
                readOnly: true,
              },
              temperature: {
                type: ['number', 'null'],
                title: 'Temperature',
                default: 0,
              },
              log_prompts: {
                type: 'boolean',
                title: 'Log prompts',
                description: 'If enabled, the generated prompts will be logged for each row.',
                default: false,
              },
            },
            required: ['column_name', 'model_id', 'prompt_id', 'prompt_revision'],
            dependencies: {
              prompt_id: {
                oneOf: revisionsOptions,
              },
            },
            title: 'LLMSummarizeConfiguration',
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          'ui:order': ['*', 'log_prompts'],
          column_name: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
          prompt_id: {
            'ui:widget': PromptWidget,
          },
          model_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': {
              autocomplete: true,
              render: (modelId: string) => <ModelRenderer modelId={modelId} models={args.promptsModels!} />,
            },
          },
        },
      },
    },
  } as StepConfig;
});

const schemaLLMSummarizeSchemaProperty: JSONSchema7 = {
  type: 'object',
  properties: {
    column_name: {
      type: 'string',
      title: 'Column Name',
      format: CustomFormat.ColumnName,
    },
    type: {
      type: 'string',
      title: 'Type',
      enum: ['string'],
      default: 'string',
    },
    description: {
      type: 'string',
      title: 'Description',
    },
    word_count: {
      type: 'number',
      title: 'Word count',
      default: 100,
    },
  },
  required: ['column_name', 'type', 'description', 'word_count'],
};

steps.set('plot_clustering', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Plot clustering',
      group: 'legacy',
      color: CATEGORY_COLORS.legacy,
      icon: <FaRegChartBar />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as PlotClusteringConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];
        const outputColumns = cfg.column_name
          ? [`embedding_${cfg.column_name}`, `cluster_label_${cfg.column_name}`]
          : [];
        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Plot Clustering',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
            },
            required: ['column_name'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
        },
      },
    },
  } as StepConfig;
});

steps.set('llm_cluster', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Cluster',
      group: 'legacy',
      color: CATEGORY_COLORS.legacy,
      icon: <FaLayerGroup />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as LLMClusterConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];
        const outputColumns = cfg.column_name ? [`cluster_label_${cfg.column_name}`] : [];
        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'LLM Cluster',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
              max_clusters: {
                type: 'number',
                title: 'Max clusters',
              },
              random_state: {
                type: 'number',
                title: 'Random state',
              },
            },
            required: ['column_name'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
        },
      },
    },
  } as StepConfig;
});

steps.set('llm_map_reduce', (args = {}): StepConfig => {
  const prompts = args.prompts ?? [];
  const promptsModelsOptions = getPromptsModelsOptions(args.promptsModels);
  const revisionsOptions = getPromptsRevisionsOptions(prompts, true, ['text', 'dependencies']);
  const maxRevision = Math.max(...(prompts ?? []).map((prompt) => prompt.revision));
  return {
    nodeConfig: {
      name: 'Hierarchical prompt',
      group: 'llm',
      color: CATEGORY_COLORS.llm,
      icon: <FaSitemap />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as LLMHierarchicalPromptConfiguration;
        const inputColumns: string[] = [];
        if (cfg.input_data) {
          for (const key in cfg.input_data) {
            const columnName = cfg.input_data[key];
            if (columnName && columnName.trim() !== '') {
              inputColumns.push(columnName);
            }
          }
        }
        if (cfg.column_name) {
          inputColumns.push(cfg.column_name);
        }
        if (cfg.identifier_column) {
          inputColumns.push(cfg.identifier_column);
        }
        if (cfg.dependencies_column) {
          inputColumns.push(cfg.dependencies_column);
        }
        const outputColumns = cfg.result_column ? [cfg.result_column] : [];
        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      customForm: LLMCommonForm,
      jsonSchema: {
        title: 'LLM hierarchical prompt',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            description:
              'Process rows in respect to their dependencies. The {dependencies} input of the prompt will contain processed entries of dependent rows.',
            $ref: '#/definitions/LLMHierarchicalPromptConfiguration',
          },
        },
        definitions: {
          LLMHierarchicalPromptConfiguration: {
            type: 'object',
            properties: {
              mode: {
                type: 'string',
                title: 'Mode',
                anyOf: [
                  { enum: [HierarchicalPromptMode.BOTTOM_UP], title: 'Bottom-Up' },
                  { enum: [HierarchicalPromptMode.TOP_DOWN], title: 'Top-Down' },
                ],
                default: HierarchicalPromptMode.BOTTOM_UP,
                description:
                  'Controls whether dependencies are processed first (bottom-up) or the row itself is processed first (top-down). In top-down mode the dependencies in the prompt are the parents of the given row.',
              },
              model_id: {
                type: 'string',
                title: 'Prompts model',
                description: 'Primary model',
                anyOf: promptsModelsOptions,
              },
              temperature: {
                type: ['number', 'null'],
                title: 'Temperature',
                default: 0,
              },
              fallback_model_id: {
                type: ['string', 'null'],
                title: 'Fallback model',
                description: 'Fallback model',
                anyOf: [{ const: null }, ...promptsModelsOptions],
              },
              fallback_token_threshold: {},
              prompt_id: {
                type: ['string', 'null'],
                title: 'Prompt',
                description: 'The prompt must have the following inputs: {text}, {dependencies}',
              },
              prompt_revision: {
                type: 'number',
                title: 'Prompt revision',
                anyOf: Array.from({ length: maxRevision }, (_, i) => ({ const: i + 1 })).reverse(),
                readOnly: true,
              },
              input_data: {
                type: 'object',
              },
              column_name: {
                type: 'string',
                title: 'Content column name',
                format: CustomFormat.ColumnName,
                description: 'Column with the content to process. Goes to the {text} input of the prompt.',
              },
              result_column: {
                type: 'string',
                title: 'Result column name',
                format: CustomFormat.ColumnName,
                description: 'Name of the column to store the result of the prompt execution.',
              },
              identifier_column: {
                type: 'string',
                title: 'Dependency identifier column name',
                format: CustomFormat.ColumnName,
                description:
                  'Column with the dependency identifier for each row e.g. "method_id" if used after code parsing.',
              },
              dependencies_column: {
                type: 'string',
                title: 'Dependency list column name',
                format: CustomFormat.ColumnName,
                description:
                  'Column with the list of dependency ids for each row e.g. "dependencies_ids" if used after code parsing.',
              },
              log_prompts: {
                type: 'boolean',
                title: 'Log prompts',
                description: 'If enabled, the generated prompts will be logged for each row.',
                default: false,
              },
            },
            required: [
              'column_name',
              'model_id',
              'prompt_id',
              'prompt_revision',
              'result_column',
              'identifier_column',
              'dependencies_column',
            ],
            dependencies: {
              prompt_id: {
                oneOf: revisionsOptions,
              },
              fallback_model_id: {
                oneOf: [
                  {
                    properties: {
                      fallback_model_id: {
                        type: 'null',
                      },
                    },
                  },
                  {
                    properties: {
                      fallback_model_id: {
                        anyOf: promptsModelsOptions,
                      },
                      fallback_token_threshold: {
                        type: 'number',
                        title: 'Fallback token threshold',
                        description:
                          'If the number of tokens in the prompt exceeds this value, the fallback model will be used.',
                      },
                    },
                    required: ['fallback_token_threshold'],
                  },
                ],
              },
            },
            title: 'LLMHierarchicalPromptConfiguration',
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          'ui:order': ['*', 'log_prompts'],
          column_name: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
          result_column: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
          identifier_column: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
          dependencies_column: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
          mode: {
            'ui:widget': ToggleButtonWidget,
            'ui:options': {
              icons: {
                [HierarchicalPromptMode.BOTTOM_UP]: <FaArrowUp />,
                [HierarchicalPromptMode.TOP_DOWN]: <FaArrowDown />,
              },
              descriptions: {
                [HierarchicalPromptMode.BOTTOM_UP]: 'Process dependencies first, then the row itself.',
                [HierarchicalPromptMode.TOP_DOWN]: 'Process the row first, then its dependencies.',
              },
            },
          },
          prompt_id: {
            'ui:widget': PromptWidget,
            'ui:options': {
              requiredInputs: ['text', 'dependencies'],
              templateHint:
                'Dependencies format: [{"dependency": <identifier>, "result": <text generated by prompt for the identifier>}]',
            },
          },
          input_data: {
            'ui:field': PromptInputsField,
            'ui:options': {
              fixedInputs: ['text', 'dependencies'],
              columns: args.columns,
            },
          },
          model_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': {
              autocomplete: true,
              render: (modelId: string) => <ModelRenderer modelId={modelId} models={args.promptsModels!} />,
            },
          },
          fallback_model_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': {
              autocomplete: true,
              render: (modelId: string) => <ModelRenderer modelId={modelId} models={args.promptsModels!} />,
            },
          },
        },
      },
    },
  } as StepConfig;
});

const getLLMResearchDataConfig = (
  models: PromptModel[],
  revisionsOptions: PromptRevisionOption[],
  maxRevision: number,
  columns?: NodeColumnsInfo[],
  promptInputsAllowed = true,
) => ({
  getColumnsInfo: (data: WorkflowNode) => {
    const cfg = data.configuration as LLMResearchConfiguration;
    const inputCols: string[] = [];
    if (cfg.input_data) {
      for (const key in cfg.input_data) {
        const columnName = cfg.input_data[key];
        if (columnName && columnName.trim() !== '') {
          inputCols.push(columnName);
        }
      }
    }
    const outputCols: string[] = [];
    if (cfg.result_column) {
      outputCols.push(cfg.result_column);
    }
    if (cfg.search_results_column) {
      outputCols.push(cfg.search_results_column);
    }
    if (cfg.thinking_results_column) {
      outputCols.push(cfg.thinking_results_column);
    }
    if (!promptInputsAllowed) {
      outputCols.push(INDEX_COLUMN);
    }
    return {
      id: data.id,
      inputStepName: data.name,
      inputColumns: inputCols,
      outputColumns: outputCols,
      removedColumns: [],
      replacesDataframe: false,
    };
  },
  customForm: LLMCommonForm,
  jsonSchema: {
    title: 'LLM Research',
    type: 'object',
    required: ['name'],
    properties: {
      name: { type: 'string', title: 'Name' },
      description: { type: 'string', title: 'Description' },
      configuration: {
        title: 'Configuration',
        $ref: '#/definitions/LLMResearchConfiguration',
      },
    },
    definitions: {
      LLMResearchConfiguration: {
        type: 'object',
        properties: {
          model_id: {
            type: 'string',
            title: 'Prompts model',
            anyOf: getPromptsModelsOptions(models, ModelType.PERPLEXITY),
            description: 'Currently only Perplexity Sonar models are supported.',
          },
          prompt_id: {
            type: ['string', 'null'],
            title: 'Prompt',
            description: !promptInputsAllowed
              ? 'Only prompts without inputs are allowed in the loader research step.'
              : '',
          },
          prompt_revision: {
            type: 'number',
            title: 'Prompt revision',
            anyOf: Array.from({ length: maxRevision }, (_, i) => ({ const: i + 1 })).reverse(),
            readOnly: true,
          },
          result_column: {
            type: 'string',
            title: 'Result column name',
            format: CustomFormat.ColumnName,
            description: 'Name of the column to store the result of the prompt execution.',
          },
          search_results_column: {
            type: ['string', 'null'],
            title: 'Search results column name',
            format: CustomFormat.ColumnName,
            description: 'Name of the column to store the search results used as context for the prompt.',
          },
          thinking_results_column: {
            type: ['string', 'null'],
            title: 'Thinking results column name',
            format: CustomFormat.ColumnName,
            description: 'Name of the column to store the reasoning steps (if provided by the model).',
          },
          temperature: {
            type: ['number', 'null'],
            title: 'Temperature',
            default: 0,
          },
          input_data: {
            type: 'object',
          },
          options: {
            $ref: '#/definitions/PerplexityOptions',
          },
          log_prompts: {
            type: 'boolean',
            title: 'Log prompts',
            description: 'If enabled, the generated prompts will be logged for each row.',
            default: false,
          },
        },
        required: ['model_id', 'prompt_id', 'prompt_revision', 'result_column'],
        dependencies: {
          prompt_id: {
            oneOf: revisionsOptions,
          },
        },
        title: 'LLMResearchConfiguration',
      },
      PerplexityOptions: {
        type: 'object',
        title: 'Search Options',
        properties: {
          search_mode: {
            type: 'string',
            title: 'Search mode',
            description:
              "When set to 'academic', results will prioritize scholarly sources like peer-reviewed papers and academic journals.",
            enum: ['web', 'academic'],
            default: 'web',
          },
          reasoning_effort: {
            type: 'string',
            title: 'Reasoning effort',
            description:
              "Controls how much computational effort the AI dedicates to each query. (Applies to 'sonar-deep-research' model only)",
            enum: ['low', 'medium', 'high'],
          },
          search_domain_filter: {
            type: 'array',
            title: 'Search domain filter',
            description:
              "Restricts search results to the specified domains or URLs. This can be an allowlist or denylist if entries are prefixed with '-'. Examples: ['wikipedia.org', 'github.com'], ['https://en.wikipedia.org/wiki/Chess', 'https://docs.python.org/3/tutorial/'], ['-reddit.com', '-quora.com']",
            items: {
              type: 'string',
              title: 'Domain',
            },
          },
          search_recency_filter: {
            type: 'string',
            title: 'Search recency filter',
            description: 'Filters results by predefined time periods relative to the current date.',
            enum: ['day', 'week', 'month', 'year'],
          },
          search_after_date_filter: {
            type: 'string',
            title: 'Search after date filter',
            description: 'Filters search results to only include content published after this date.',
          },
          search_before_date_filter: {
            type: 'string',
            title: 'Search before date filter',
            description: 'Filters search results to only include content published before this date.',
          },
          last_updated_after_filter: {
            type: 'string',
            title: 'Last updated after filter',
            description: 'Filters search results to only include content last updated after this date.',
          },
          last_updated_before_filter: {
            type: 'string',
            title: 'Last updated before filter',
            description: 'Filters search results to only include content last updated before this date.',
          },
          web_search_options: {
            $ref: '#/definitions/PerplexityWebSearchOptions',
          },
        },
      },
      PerplexityWebSearchOptions: {
        type: 'object',
        title: 'Web Search Options',
        properties: {
          search_context_size: {
            type: 'string',
            title: 'Search context size',
            description: 'Determines how much search context is retrieved for the model.',
            enum: ['low', 'medium', 'high'],
            default: 'low',
          },
          user_location: {
            $ref: '#/definitions/UserLocationOptions',
          },
        },
      },
      UserLocationOptions: {
        type: 'object',
        title: 'User Location Options',
        properties: {
          country: {
            type: 'string',
            title: 'Country',
            description: "The two letter ISO country code of the user's location. (e.g., 'US', 'FR', 'JP').",
          },
          region: {
            type: 'string',
            title: 'Region',
            description:
              "The region/state/province of the user's location (e.g., 'California', 'Ontario', 'Île-de-France').",
          },
          city: {
            type: 'string',
            title: 'City',
            description: "The city name of the user's location (e.g., 'San Francisco', 'New York City', 'Paris').",
          },
        },
      },
    },
  },
  uiSchema: {
    description: {
      'ui:autocomplete': 'off',
    },
    configuration: {
      'ui:order': ['*', 'log_prompts'],
      input_data: {
        'ui:field': PromptInputsField,
        'ui:options': { columns },
      },
      prompt_id: {
        'ui:widget': PromptWidget,
        'ui:options': { inputsAllowed: promptInputsAllowed },
      },
      model_id: {
        'ui:widget': ListSelectionWidget,
        'ui:options': {
          autocomplete: true,
          render: (modelId: string) => <ModelRenderer modelId={modelId} models={models} />,
        },
      },
      options: {
        search_after_date_filter: { 'ui:widget': PerplexityDateFormatWidget },
        search_before_date_filter: { 'ui:widget': PerplexityDateFormatWidget },
        last_updated_after_filter: { 'ui:widget': PerplexityDateFormatWidget },
        last_updated_before_filter: { 'ui:widget': PerplexityDateFormatWidget },
      },
    },
  },
});

steps.set('llm_research', (args = {}): StepConfig => {
  const inputColumns = args.columns;
  const prompts = args.prompts ?? [];
  const models = args.promptsModels ?? [];
  const revisionsOptions = getPromptsRevisionsOptions(prompts, true);
  const maxRevision = Math.max(...(prompts ?? []).map((prompt) => prompt.revision));
  return {
    nodeConfig: {
      name: 'Research',
      group: 'llm',
      color: CATEGORY_COLORS.llm,
      icon: <FaMicroscope />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: getLLMResearchDataConfig(models, revisionsOptions, maxRevision, inputColumns),
  } as StepConfig;
});

steps.set('coding_agent', (args = {}): StepConfig => {
  const prompts = args.prompts ?? [];
  const revisionsOptions = getPromptsRevisionsOptions(prompts);
  const evaluationRevisionsOptions = getEvaluationPromptsRevisionsOptions(prompts);
  const promptsModelsOptions = getPromptsModelsOptions(args.promptsModels);
  const openAiModelsOptions = getPromptsModelsOptions(args.promptsModels, ModelType.OPEN_AI);
  const anthropicModelsOptions = getPromptsModelsOptions(args.promptsModels, ModelType.ANTHROPIC);
  const maxRevision = Math.max(...(prompts ?? []).map((prompt) => prompt.revision));

  return {
    nodeConfig: {
      name: 'Coding Agent',
      group: 'advanced',
      color: CATEGORY_COLORS.advanced,
      icon: <RiRobot2Fill />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as CodingAgentConfiguration;
        const inputColumns: string[] = [];
        if (cfg.file_name_column) {
          inputColumns.push(cfg.file_name_column);
        }
        if (cfg.file_path_column) {
          inputColumns.push(cfg.file_path_column);
        }
        if (cfg.file_content_column) {
          inputColumns.push(cfg.file_content_column);
        }

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns: [],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      customForm: CodingAgentForm,
      jsonSchema: {
        title: 'Coding Agent',
        type: 'object',
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            properties: {
              agent_type: {
                type: 'string',
                title: 'Agent type',
                anyOf: [
                  { const: CodingAgentType.CODEX, title: 'Codex' },
                  { const: CodingAgentType.CLAUDE, title: 'Claude Code' },
                  { const: CodingAgentType.OPENCODE, title: 'OpenCode' },
                ],
                default: CodingAgentType.CODEX,
              },
              model_id: {
                type: ['string', 'null'],
                title: 'Model',
                description: 'Model to use for the coding agent.',
              },
              prompt_id: {
                type: 'string',
                title: 'Prompt',
                anyOf: prompts.map((prompt) => ({ const: prompt.id, title: prompt.prompt_name })),
                description: 'Only prompts without inputs are allowed in the coding agent step.',
              },
              prompt_revision: {
                type: 'number',
                title: 'Prompt revision',
                anyOf: Array.from({ length: maxRevision }, (_, i) => ({ const: i + 1 })).reverse(),
                readOnly: true,
              },
              file_name_column: {
                type: 'string',
                title: 'File name column',
                format: CustomFormat.ColumnName,
              },
              file_path_column: {
                type: 'string',
                title: 'File path column',
                format: CustomFormat.ColumnName,
              },
              file_content_column: {
                type: 'string',
                title: 'File content column',
                format: CustomFormat.ColumnName,
              },
              binary: {
                type: 'boolean',
                title: 'Binary content?',
                description: 'Whether the file contents are in binary format.',
                default: false,
              },
              tool_control: {
                type: 'object',
                title: 'Tool control',
                description: 'Restrict which tools the coding agent can use.',
                properties: {
                  mode: {
                    type: 'string',
                    title: 'Tool access',
                    anyOf: [
                      { const: ToolControlMode.ALLOW_ALL, title: 'Allow all tools' },
                      { const: ToolControlMode.ALLOWLIST, title: 'Allow selected tools' },
                      { const: ToolControlMode.DENYLIST, title: 'Deny selected tools' },
                    ],
                    default: ToolControlMode.ALLOW_ALL,
                  },
                },
                required: ['mode'],
                dependencies: {
                  mode: {
                    oneOf: [
                      {
                        properties: {
                          mode: {
                            const: ToolControlMode.ALLOW_ALL,
                          },
                        },
                      },
                      {
                        properties: {
                          mode: {
                            enum: [ToolControlMode.ALLOWLIST, ToolControlMode.DENYLIST],
                          },
                          tools: {
                            type: 'array',
                            title: 'Tools',
                            uniqueItems: true,
                            minItems: 1,
                            items: {
                              type: 'string',
                              anyOf: [
                                { const: CodingAgentTool.FILE_READ, title: 'File read' },
                                { const: CodingAgentTool.FILE_EDIT, title: 'File edit' },
                                { const: CodingAgentTool.SHELL, title: 'Shell' },
                                { const: CodingAgentTool.WEB_ACCESS, title: 'Web access' },
                                { const: CodingAgentTool.SUBAGENTS, title: 'Subagents' },
                              ],
                            },
                          },
                        },
                        required: ['tools'],
                      },
                    ],
                  },
                },
              },
              mcp_servers: {
                type: 'array',
                default: [],
                items: {
                  type: 'object',
                  properties: {
                    name: { type: 'string', title: 'Name' },
                    transport: {
                      type: 'string',
                      title: 'Transport',
                      enum: [McpTransport.HTTP, McpTransport.SSE],
                      default: McpTransport.HTTP,
                    },
                    url: { type: 'string', title: 'URL' },
                    headers: {
                      type: 'object',
                      additionalProperties: { type: 'string' },
                      default: {},
                    },
                  },
                  required: ['name', 'url'],
                },
              },
              expand_evaluation_settings: {
                type: 'boolean',
                title: 'Evaluation',
                description: 'Show additional settings for coding agent results evaluation.',
                default: false,
              },
            },
            dependencies: {
              prompt_id: {
                oneOf: revisionsOptions,
              },
              evaluation_prompt_id: {
                oneOf: evaluationRevisionsOptions,
              },
              agent_type: {
                oneOf: [
                  {
                    properties: {
                      agent_type: {
                        const: CodingAgentType.CODEX,
                      },
                      model_id: {
                        type: ['string', 'null'],
                        title: 'Model',
                        description: 'Model to use for the coding agent. Only OpenAI models are supported for Codex.',
                        anyOf: [{ const: null }, ...openAiModelsOptions],
                      },
                      secret_id: {
                        type: ['string', 'null'],
                        title: 'Coding Agent credentials or API key',
                        description:
                          'Credentials for the coding agent. Can be either Coding Agent Credentials or an API key. Required when no model is selected.',
                      },
                    },
                    allOf: [
                      {
                        if: {
                          properties: {
                            model_id: { type: 'null' },
                          },
                        },
                        then: {
                          required: ['secret_id'],
                        },
                      },
                    ],
                  },
                  {
                    properties: {
                      agent_type: {
                        const: CodingAgentType.OPENCODE,
                      },
                      model_id: {
                        type: 'string',
                        title: 'Model',
                        description: 'Model to use for the coding agent.',
                        anyOf: promptsModelsOptions,
                      },
                    },
                    required: ['model_id'],
                  },
                  {
                    properties: {
                      agent_type: {
                        const: CodingAgentType.CLAUDE,
                      },
                      model_id: {
                        type: ['string', 'null'],
                        title: 'Model',
                        description:
                          'Model to use for the coding agent. Only Anthropic models are supported for Claude Code.',
                        anyOf: [{ const: null }, ...anthropicModelsOptions],
                      },
                      secret_id: {
                        type: ['string', 'null'],
                        title: 'Coding Agent credentials or API key',
                        description:
                          'Credentials for the coding agent. Can be either Coding Agent Credentials or an API key. Required when no model is selected.',
                      },
                    },
                    allOf: [
                      {
                        if: {
                          properties: {
                            model_id: { type: 'null' },
                          },
                        },
                        then: {
                          required: ['secret_id'],
                        },
                      },
                    ],
                  },
                ],
              },
              expand_evaluation_settings: {
                oneOf: [
                  {
                    properties: {
                      expand_evaluation_settings: {
                        const: false,
                      },
                    },
                  },
                  {
                    properties: {
                      expand_evaluation_settings: {
                        const: true,
                      },
                      evaluation_model_id: {
                        type: 'string',
                        title: 'Evaluation model',
                        anyOf: promptsModelsOptions,
                      },
                      evaluation_prompt_id: {
                        type: 'string',
                        title: 'Evaluation prompt',
                        anyOf: prompts.map((prompt) => ({ const: prompt.id, title: prompt.prompt_name })),
                        description: 'The prompt must have the following inputs: {prompt}, {response}.',
                      },
                      evaluation_prompt_revision: {
                        type: 'number',
                        title: 'Evaluation prompt revision',
                        anyOf: Array.from({ length: maxRevision }, (_, i) => ({ const: i + 1 })).reverse(),
                        readOnly: true,
                      },
                      evaluation_operator: {
                        type: 'string',
                        title: 'Evaluation operator',
                        enum: [
                          '=',
                          '!=',
                          'CONTAINS',
                          'NOT CONTAINS',
                          'STARTS WITH',
                          'ENDS WITH',
                          'CONTAINS WORD',
                          'NOT CONTAINS WORD',
                          'STARTS WITH WORD',
                          'ENDS WITH WORD',
                        ],
                        default: '=',
                        description:
                          'The operator to compare the evaluation prompt output with the expected value. The comparison is case insensitive.',
                      },
                      evaluation_expected_value: {
                        type: 'string',
                        title: 'Evaluation expected value',
                        description: 'The expected value to compare the evaluation prompt output with.',
                      },
                    },
                    required: [
                      'evaluation_model_id',
                      'evaluation_prompt_id',
                      'evaluation_prompt_revision',
                      'evaluation_operator',
                      'evaluation_expected_value',
                    ],
                  },
                ],
              },
            },
            required: [
              'agent_type',
              'prompt_id',
              'prompt_revision',
              'file_name_column',
              'file_path_column',
              'file_content_column',
            ],
          },
        },
        required: ['name'],
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          'ui:order': [
            'agent_type',
            'model_id',
            'secret_id',
            '*',
            'tool_control',
            'mcp_servers',
            'expand_evaluation_settings',
            'evaluation_model_id',
            'evaluation_prompt_id',
            'evaluation_prompt_revision',
            'evaluation_operator',
            'evaluation_expected_value',
            'binary',
          ],
          agent_type: {
            'ui:widget': ListSelectionWidget,
          },
          mcp_servers: {
            'ui:field': McpServersField,
          },
          tool_control: {
            mode: {
              'ui:widget': ListSelectionWidget,
            },
            tools: {
              'ui:widget': MultiSelectWidget,
              'ui:options': {
                placeholder: 'Select tools',
                // Codex always has access to the file read tool and it cannot be disabled.
                lockedValues: (formData: CreateWorkflowNode | undefined) => {
                  const cfg = formData?.configuration as CodingAgentConfiguration | undefined;
                  return cfg?.agent_type === CodingAgentType.CODEX &&
                    cfg?.tool_control?.mode === ToolControlMode.ALLOWLIST
                    ? [CodingAgentTool.FILE_READ]
                    : [];
                },
                blockedValues: (formData: CreateWorkflowNode | undefined) => {
                  const cfg = formData?.configuration as CodingAgentConfiguration | undefined;
                  return cfg?.agent_type === CodingAgentType.CODEX &&
                    cfg?.tool_control?.mode === ToolControlMode.DENYLIST
                    ? [CodingAgentTool.FILE_READ]
                    : [];
                },
                noticeText: (formData: CreateWorkflowNode | undefined) => {
                  const cfg = formData?.configuration as CodingAgentConfiguration | undefined;
                  return cfg?.agent_type === CodingAgentType.CODEX
                    ? 'Codex always has access to the file read tool - it cannot be disabled.'
                    : undefined;
                },
              },
            },
          },
          model_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': {
              autocomplete: true,
              render: (modelId: string) => <ModelRenderer modelId={modelId} models={args.promptsModels!} />,
            },
          },
          prompt_id: {
            'ui:widget': PromptWidget,
            'ui:options': { inputsAllowed: false },
          },
          secret_id: {
            'ui:widget': SecretWidget,
            'ui:options': {
              types: ['api_key', 'coding_agent_credentials'],
            },
          },
          file_name_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          file_path_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          file_content_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          evaluation_model_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': {
              autocomplete: true,
              render: (modelId: string) => <ModelRenderer modelId={modelId} models={args.promptsModels!} />,
            },
          },
          evaluation_prompt_id: {
            'ui:widget': PromptWidget,
            'ui:options': {
              requiredInputs: ['prompt', 'response'],
              additionalInputsAllowed: false,
              templateHint:
                'The {prompt} input will be populated with the original prompt and the {response} input will be populated with the coding agent response. The evaluation prompt should output a value that can be compared to the expected value using the selected operator.',
            },
          },
        },
      },
    },
  } as StepConfig;
});

/* *** steps - data operations */

steps.set('keyword_matching', (args = {}): StepConfig => {
  const filesOptions = getFilesOptions(args.files, JSON_FILE_TYPES);
  return {
    nodeConfig: {
      name: 'Keyword matching',
      group: 'text_processing',
      color: CATEGORY_COLORS.text_processing,
      icon: <FaKey />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as KeywordMatchingConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];
        const outputColumns = cfg.result_column ? [cfg.result_column, `${cfg.result_column}_json`] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Keyword Matching',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
              result_column: {
                type: 'string',
                title: 'Result column name',
                format: CustomFormat.ColumnName,
              },
              case_sensitive: {
                type: 'boolean',
                title: 'Case sensitive',
                default: false,
              },
              keyword_label_map_file_path: {
                type: ['string', 'null'],
                title: 'Label keywords file',
                description:
                  'JSON file with label to keywords mapping, e.g. {"animal": ["cat", "dog"], "vehicle": ["car", "bike"]}',
                anyOf: [{ const: null }, ...filesOptions],
              },
              keyword_label_map: {
                type: 'object',
                title: 'Label keywords map',
                description: 'Labels added here will be included on top of the ones from the file.',
                additionalProperties: {
                  type: 'array',
                  items: {
                    type: 'string',
                  },
                },
              },
            },
            required: ['column_name', 'keyword_label_map', 'result_column'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          keyword_label_map_file_path: {
            'ui:widget': FileManagementWidget,
            'ui:options': {
              accept: '.json',
            },
          },
          case_sensitive: {
            'ui:widget': 'radio',
            'ui:options': {
              inline: true,
            },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('explode', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Explode',
      group: 'transformers',
      color: CATEGORY_COLORS.transformers,
      icon: <FaArrowsUpDownLeftRight />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
      definesExpectedColumns: true,
    },
    dataConfig: {
      // We cannot determine output columns of this operation.
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as ExplodeConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns: [],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Explode',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
              data_format: {
                type: 'string',
                title: 'Data format',
                enum: ['json', 'xml'],
              },
              explode_target: {
                type: 'string',
                title: 'Where to explode data',
                enum: ['columns', 'rows'],
                default: 'columns',
              },
              properties_to_explode: {
                type: 'string',
                title: 'Properties to explode',
                enum: ['all', 'custom_selection', 'from_column'],
              },
            },
            required: ['column_name', 'data_format', 'explode_target', 'properties_to_explode'],
            dependencies: {
              properties_to_explode: {
                oneOf: [
                  {
                    properties: {
                      properties_to_explode: {
                        enum: ['all'],
                      },
                    },
                  },
                  {
                    properties: {
                      properties_to_explode: {
                        enum: ['from_column'],
                      },
                      properties_column_name: {
                        type: 'string',
                        title: 'Properties column name',
                      },
                    },
                    required: ['properties_column_name'],
                  },
                  {
                    properties: {
                      properties_to_explode: {
                        enum: ['custom_selection'],
                      },
                      properties: {
                        type: 'array',
                        title: 'Properties',
                        items: {
                          type: 'string',
                        },
                      },
                    },
                    required: ['properties'],
                  },
                ],
              },
            },
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          properties_column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('regex_find', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Regex Find',
      group: 'text_processing',
      color: CATEGORY_COLORS.text_processing,
      icon: <FaMagnifyingGlass />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as RegexFindConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];
        const outputColumns = cfg.result_column ? [cfg.result_column] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Regular expression find',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Input text column name',
                format: CustomFormat.ColumnName,
              },
              regex_pattern: {
                type: 'string',
                title: 'Search regular expression',
              },
              mode: {
                type: 'string',
                title: 'Matching group selection',
                enum: ['first', 'all'],
              },
              result_column: {
                type: 'string',
                title: 'Result column name',
                format: CustomFormat.ColumnName,
              },
            },
            required: ['column_name', 'regex_pattern', 'mode', 'result_column'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('group_merge', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Merge grouped data',
      group: 'transformers',
      color: CATEGORY_COLORS.transformers,
      icon: <FaBuffer />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as GroupMergeConfiguration;
        const inputColumns: string[] = [];
        if (cfg.grouping_column) {
          inputColumns.push(cfg.grouping_column);
        }
        if (cfg.columns_to_include) {
          inputColumns.push(...cfg.columns_to_include);
        }

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns: [INDEX_COLUMN, 'group_key', 'merged_text'],
          removedColumns: [],
          replacesDataframe: true,
        };
      },
      jsonSchema: {
        title: 'Merge grouped data',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              columns_to_include: {
                type: 'array',
                items: {
                  type: 'string',
                  format: CustomFormat.ColumnName,
                },
                title: 'Columns to merge',
              },
              grouping_column: {
                type: ['string', 'null'],
                title: 'Group by column',
                description: 'Merges all rows when the group by column is not selected',
                format: CustomFormat.ColumnName,
              },
              grouping_col_condition: {
                type: ['string', 'null'],
                title: 'Grouping column condition',
                description: 'Group only rows with this value',
              },
              template: {
                type: ['string', 'null'],
                title: 'Columns merge template',
                description:
                  'Reference columns by {column_name}. If not provided, columns will be concatenated with a coma.',
              },
              process_arrays: {
                type: 'boolean',
                title: 'Process arrays inside grouping column ?',
                default: false,
              },
            },
            required: ['columns_to_include'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          columns_to_include: {
            items: {
              'ui:widget': ColumnFromFileWidget,
              'ui:options': { columns: args.columns },
            },
          },
          grouping_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          template: {
            'ui:widget': ResizableTextarea,
            'ui:options': {
              rows: 4,
              style: {
                resize: 'vertical',
              },
            },
          },
          process_arrays: {
            'ui:widget': 'radio',
            'ui:options': {
              inline: true,
            },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('mapping', (args = {}): StepConfig => {
  const filesOptions = getFilesOptions(args.files, JSON_FILE_TYPES);
  return {
    nodeConfig: {
      name: 'Mapping',
      group: 'transformers',
      color: CATEGORY_COLORS.transformers,
      icon: <FaArrowRight />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as MappingConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];
        const outputColumns = cfg.result_column ? [cfg.result_column] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Mapping',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
              result_column: {
                type: 'string',
                title: 'Result column name',
                format: CustomFormat.ColumnName,
              },
              case_sensitive: {
                type: 'boolean',
                title: 'Case sensitive',
                default: false,
              },
              regex: {
                type: 'boolean',
                title: 'Use regex',
                description: 'You can reference regex capturing groups in the mapping with \\1, \\2, etc.',
                default: false,
              },
              map_file_path: {
                type: ['string', 'null'],
                title: 'Map file',
                description: 'JSON file containing the mapping.',
                anyOf: [{ const: null }, ...filesOptions],
              },
              map_dict: {
                type: 'object',
                title: 'Map',
                description: 'Mappings added here will be added on top of the ones from the file.',
                additionalProperties: {
                  type: 'string',
                },
              },
            },
            required: ['column_name', 'result_column', 'map_dict'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          map_file_path: {
            'ui:widget': FileManagementWidget,
            'ui:options': {
              accept: '.json',
            },
          },
          case_sensitive: {
            'ui:widget': 'radio',
            'ui:options': {
              inline: true,
            },
          },
          regex: {
            'ui:widget': 'radio',
            'ui:options': {
              inline: true,
            },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('sampling_filter', (): StepConfig => {
  return {
    nodeConfig: {
      name: 'Sampling filter',
      group: 'filters',
      color: CATEGORY_COLORS.filters,
      icon: <FaDice />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: emptyColumnsInfo,
      jsonSchema: {
        title: 'Sampling Filter',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              sampling_strategy: {
                type: 'string',
                title: 'Sampling Strategy',
                enum: ['top', 'bottom', 'random', 'range'],
              },
            },
            required: ['sampling_strategy'],
            dependencies: {
              sampling_strategy: {
                oneOf: [
                  {
                    properties: {
                      sampling_strategy: {
                        enum: ['top', 'bottom', 'random'],
                      },
                      sample_size: {
                        type: 'number',
                        title: 'Sample Size',
                        minimum: 1,
                      },
                    },
                    required: ['sample_size'],
                  },
                  {
                    properties: {
                      sampling_strategy: {
                        enum: ['range'],
                      },
                      range_start: {
                        type: 'number',
                        title: 'Range Start',
                        description: 'Start of the range. Indexing starts from 0.',
                        minimum: 0,
                      },
                      // TODO: add range_end value validation
                      // - it should be greater or equal to range_start
                      // - (optional) it should be less or equal to number of rows in the table
                      //    optional due to the fact that passing a value greater than the number of rows still will return selected rows
                      range_end: {
                        type: 'number',
                        title: 'Range End',
                        description: 'End of the range (inclusive). Should be greater or equal to range_start.',
                        minimum: 1,
                      },
                    },
                    required: ['range_start', 'range_end'],
                  },
                ],
              },
            },
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
      },
    },
  } as StepConfig;
});

const conditionConfiguration: JSONSchema7 = {
  title: 'Configuration',
  type: 'object',
  properties: {
    column_name: {
      type: 'string',
      title: 'Column Name',
      format: CustomFormat.ColumnName,
    },
    operator: {
      type: 'string',
      title: 'Operator',
      enum: [
        '=',
        '!=',
        '>',
        '<',
        '>=',
        '<=',
        'CONTAINS',
        'NOT CONTAINS',
        'STARTS WITH',
        'ENDS WITH',
        'IN',
        'NOT IN',
        'IS NULL',
        'IS NOT NULL',
      ],
    },
  },
  required: ['column_name', 'operator'],
  dependencies: {
    operator: {
      oneOf: [
        {
          properties: {
            operator: {
              enum: ['=', '!=', '>', '<', '>=', '<=', 'CONTAINS', 'NOT CONTAINS', 'STARTS WITH', 'ENDS WITH'],
            },
            filter_value: {
              type: 'string',
              title: 'Filter Value',
            },
          },
          required: ['filter_value'],
        },
        {
          properties: {
            operator: {
              enum: ['IN', 'NOT IN'],
            },
            in_values: {
              type: 'array',
              items: {
                type: 'string',
                title: 'IN/NOT IN values',
              },
              minItems: 1,
            },
          },
          required: ['in_values'],
        },
        {
          properties: {
            operator: {
              enum: ['IS NULL', 'IS NOT NULL'],
            },
          },
        },
      ],
    },
  },
};

steps.set('simple_filter', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Simple filter',
      group: 'filters',
      color: CATEGORY_COLORS.filters,
      icon: <FaFilter />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as SimpleFilterConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns: [],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Simple Filter',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: conditionConfiguration,
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('query_filter', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Query filter',
      group: 'filters',
      color: CATEGORY_COLORS.filters,
      icon: <FaMagnifyingGlass />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as QueryFilterConfiguration;
        const inputColumns: string[] = [];
        const gatherColumns = (group: ConditionGroup) => {
          if (group.conditions) {
            group.conditions.forEach((condition) => {
              if (condition.column_name && !inputColumns.includes(condition.column_name)) {
                inputColumns.push(condition.column_name);
              }
            });
          }
          if (group.groups) {
            group.groups.forEach((subgroup) => gatherColumns(subgroup));
          }
        };
        if (cfg.condition_group) {
          gatherColumns(cfg.condition_group);
        }

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns: [],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Query Filter',
        type: 'object',
        required: ['name'],
        definitions: {
          condition_group: {
            type: 'object',
            title: 'Condition Group',
            description: 'Define conditions to filter data',
            properties: {
              operator: {
                type: 'string',
                title: 'Operator',
                enum: ['AND', 'OR'],
                default: 'AND',
              },
              conditions: {
                type: 'array',
                items: conditionConfiguration,
                title: 'Conditions',
              },
              groups: {
                type: 'array',
                items: { $ref: '#/definitions/condition_group' },
                title: 'Condition Groups',
              },
            },
            required: ['conditions'],
          },
        },
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              condition_group: {
                $ref: '#/definitions/condition_group',
              },
            },
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          condition_group: {
            'ui:field': QueryBuilderField,
            'ui:options': { columns: args.columns },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('drop_property', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Drop column',
      group: 'transformers',
      color: CATEGORY_COLORS.transformers,
      icon: <FaDeleteLeft />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as DropPropertyConfiguration;
        const columnsToDrop = cfg.column_names ?? [];
        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns: columnsToDrop,
          outputColumns: [],
          removedColumns: columnsToDrop,
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Drop column',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_names: {
                type: 'array',
                items: {
                  type: 'string',
                  format: CustomFormat.ColumnName,
                },
                title: 'Columns to drop',
                minItems: 1,
              },
            },
            required: ['column_names'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_names: {
            items: {
              'ui:widget': ColumnFromFileWidget,
              'ui:options': { columns: args.columns },
            },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('rename_column', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Rename column',
      group: 'transformers',
      color: CATEGORY_COLORS.transformers,
      icon: <FaPenToSquare />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as RenameColumnConfiguration;
        const inputColumns = cfg.columns ? cfg.columns.map((col) => col.old_name) : [];
        const outputColumns = cfg.columns ? cfg.columns.map((col) => col.new_name) : [];
        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: inputColumns,
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Rename column',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              columns: {
                type: 'array',
                items: {
                  type: 'object',
                  title: '',
                  properties: {
                    old_name: {
                      type: 'string',
                      title: 'Old column name',
                      format: CustomFormat.ColumnName,
                    },
                    new_name: {
                      type: 'string',
                      title: 'New column name',
                      format: CustomFormat.ColumnName,
                    },
                  },
                  required: ['old_name', 'new_name'],
                },
                title: 'Columns to rename',
                minItems: 1,
              },
            },
            required: ['columns'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          columns: {
            items: {
              old_name: {
                'ui:widget': ColumnFromFileWidget,
                'ui:options': { columns: args.columns },
              },
            },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('json_to_markdown', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'JSON to Markdown',
      group: 'text_processing',
      color: CATEGORY_COLORS.text_processing,
      icon: <FaMarkdown />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as JSONToMarkdownConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];
        const outputColumns = cfg.result_column ? [cfg.result_column] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'JSON to Markdown',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                description: 'Column containing JSON data to be converted to Markdown',
                format: CustomFormat.ColumnName,
              },
              result_column: {
                type: 'string',
                title: 'Result column name',
                description: 'Column to store the converted Markdown',
                format: CustomFormat.ColumnName,
              },
            },
            required: ['column_name', 'result_column'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('python_function', (args = {}): StepConfig => {
  const functionRevisionsOptions = getFunctionRevisionsOptions(args.functions);
  const maxRevision = Math.max(...(args.functions ?? []).map((func) => func.revision));

  return {
    nodeConfig: {
      name: 'Python function',
      group: 'advanced',
      color: CATEGORY_COLORS.advanced,
      icon: <FaPython />,
      source: true,
      target: true,
      multitarget: true,
      minInputs: 1,
      definesExpectedColumns: true,
    },
    dataConfig: {
      getColumnsInfo: emptyColumnsInfo,
      customForm: PythonFunctionForm,
      jsonSchema: {
        title: 'Python Function',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              function_id: {
                type: 'string',
                title: 'Python function',
              },
              function_revision: {
                type: 'number',
                title: 'Function revision',
                anyOf: Array.from({ length: maxRevision }, (_, i) => ({ const: i + 1 })).reverse(),
                readOnly: true,
              },
            },
            required: ['function_id', 'function_revision'],
            dependencies: {
              function_id: {
                oneOf: functionRevisionsOptions,
              },
            },
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          function_id: {
            'ui:widget': PythonFunctionWidget,
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('default_value_column', (): StepConfig => {
  return {
    nodeConfig: {
      name: 'Add column with default value',
      group: 'transformers',
      color: CATEGORY_COLORS.transformers,
      icon: <FaPlusSquare />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as DefaultValueColumnConfiguration;
        const outputColumns = cfg.column_name ? [cfg.column_name] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns: [],
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Add column with default value',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
              column_type: {
                type: 'string',
                title: 'Column type',
                enum: ['string', 'integer', 'float', 'boolean'],
                default: 'string',
              },
              value: {
                type: ['string', 'number', 'boolean'],
                title: 'Default value',
              },
            },
            required: ['column_name', 'column_type'],
            dependencies: {
              column_type: {
                oneOf: [
                  {
                    properties: {
                      column_type: {
                        enum: ['string'],
                      },
                      value: {
                        type: 'string',
                        title: 'Default value (string)',
                      },
                    },
                    required: ['column_type'],
                  },
                  {
                    properties: {
                      column_type: {
                        enum: ['integer'],
                      },
                      value: {
                        type: 'integer',
                        title: 'Default value (integer)',
                      },
                    },
                    required: ['column_type'],
                  },
                  {
                    properties: {
                      column_type: {
                        enum: ['float'],
                      },
                      value: {
                        type: 'number',
                        title: 'Default value (float)',
                      },
                    },
                    required: ['column_type'],
                  },
                  {
                    properties: {
                      column_type: {
                        enum: ['boolean'],
                      },
                      value: {
                        enum: [true, false],
                        title: 'Default value (boolean)',
                      },
                    },
                    required: ['column_type'],
                  },
                ],
              },
            },
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
      },
    },
  } as StepConfig;
});

steps.set('replace_text_shortcuts', (args = {}): StepConfig => {
  const filesOptions = getFilesOptions(args.files, JSON_FILE_TYPES);
  return {
    nodeConfig: {
      name: 'Replace text shortcuts',
      group: 'text_processing',
      color: CATEGORY_COLORS.text_processing,
      icon: <FaRepeat />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as ReplaceTextShortcutsConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns: [],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Replace text shortcuts',
        description: 'Replaces shortcuts in the text by adding their descriptions e.g. ABC → ABC (description)',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
              shortcuts_file_path: {
                type: ['string', 'null'],
                title: 'Shortcuts file',
                description: 'JSON file with shortcuts mapping.',
                anyOf: [{ const: null }, ...filesOptions],
              },
              shortcuts_dict: {
                type: 'object',
                title: 'Shortcuts',
                description:
                  'Keys are shortcuts, values are descriptions. Shortcuts added here will be included on top of the ones from the file.',
                additionalProperties: {
                  type: 'string',
                },
              },
            },
            required: ['column_name'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
          shortcuts_file_path: {
            'ui:widget': FileManagementWidget,
            'ui:options': {
              accept: '.json',
            },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('remove_code_block_tags', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Remove code block tags',
      group: 'text_processing',
      color: CATEGORY_COLORS.text_processing,
      icon: <FaEraser />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as RemoveCodeBlockTagsConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns: [],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Remove code block tags',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
            },
            required: ['column_name'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
        },
      },
    },
  } as StepConfig;
});

steps.set('remove_code_comments', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Remove code comments',
      group: 'text_processing',
      color: CATEGORY_COLORS.text_processing,
      icon: <FaCommentSlash />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as RemoveCodeCommentsConfiguration;
        const inputColumns: string[] = [];
        if (cfg.code_column) {
          inputColumns.push(cfg.code_column);
        }
        if (cfg.extension_column) {
          inputColumns.push(cfg.extension_column);
        }

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns: [],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Remove code comments',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              code_column: {
                type: 'string',
                title: 'Code column',
                format: CustomFormat.ColumnName,
              },
              extension_column: {
                type: 'string',
                title: 'File extension column',
                format: CustomFormat.ColumnName,
              },
              trim_newlines: {
                type: 'boolean',
                title: 'Trim newlines?',
                default: true,
                description: 'If enabled, newlines from removed comments will be trimmed.',
              },
            },
            required: ['code_column', 'extension_column'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          code_column: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
          extension_column: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
          trim_newlines: {
            'ui:widget': 'radio',
            'ui:options': {
              inline: true,
            },
          },
        },
      },
    },
  } as StepConfig;
});

/* *** steps - flow control *** */

steps.set('merge', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Merge',
      group: 'flow_control',
      color: CATEGORY_COLORS.flow_control,
      icon: <FaCodeMerge />,
      source: true,
      target: true,
      multitarget: true,
      minInputs: 2,
      definesExpectedColumns: true,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as MergeConfiguration;
        const inputColumns = cfg.join_order ? cfg.join_order.map((item) => item.column) : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns: [],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      customForm: MergeSchemaForm,
      jsonSchema: {
        title: 'Merge',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              strategy: {
                type: 'string',
                title: 'Merge type',
                enum: ['union', 'intersect', 'join'],
              },
            },
            required: ['strategy'],
            dependencies: {
              strategy: {
                oneOf: [
                  {
                    properties: {
                      strategy: {
                        enum: ['join'],
                      },
                      join_type: {
                        type: 'string',
                        title: 'Join type',
                        enum: ['INNER', 'LEFT', 'RIGHT', 'FULL OUTER'],
                      },
                    },
                    required: ['join_type'],
                  },
                  {
                    properties: {
                      strategy: {
                        enum: ['union', 'intersect'],
                      },
                    },
                  },
                ],
              },
            },
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          join_column: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
        },
      },
    },
  } as StepConfig;
});

steps.set('embed_workflow', (args = {}): StepConfig => {
  const workflows = args.workflows;
  const workflowsOptions = getWorkflowsOptions(workflows).filter((option) => option.const !== args.workflowId);
  const revisionsOptions = getRevisionsOptions(workflows);
  const maxRevision = Math.max(...(workflows ?? []).map((workflow) => workflow.revision));
  return {
    nodeConfig: {
      name: 'Embed workflow',
      group: 'flow_control',
      color: CATEGORY_COLORS.flow_control,
      icon: <FaObjectGroup />,
      source: true,
      target: true,
      multitarget: true,
      minInputs: 1,
      definesExpectedColumns: true,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode, embeddedWorkflows?: Workflow[]) => {
        const prompts = args.prompts;
        const models = args.promptsModels;
        const files = args.files;
        return subworkflowColumnsInfo(data, embeddedWorkflows, prompts, models, files, workflows);
      },
      customForm: EmbedWorkflowForm,
      jsonSchema: {
        title: 'Embed workflow',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              workflow_id: {
                type: 'string',
                title: 'Workflow',
                anyOf: workflowsOptions,
              },
              revision: {
                type: 'number',
                title: 'Workflow revision',
                anyOf: Array.from({ length: maxRevision }, (_, i) => ({ const: i + 1 })).reverse(),
                readOnly: true,
              },
            },
            required: ['workflow_id', 'revision'],
            dependencies: {
              workflow_id: {
                oneOf: revisionsOptions,
              },
            },
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          workflow_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': { autocomplete: true },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('loop_entry', (): StepConfig => {
  return {
    nodeConfig: {
      name: 'Loop Entry',
      group: 'flow_control',
      color: CATEGORY_COLORS.flow_control,
      icon: <FaArrowRightToBracket />,
      source: true,
      target: false,
      multitarget: false,
      minInputs: 1,
      maxInstances: 1,
    },
    dataConfig: {
      getColumnsInfo: emptyColumnsInfo,
      jsonSchema: {
        title: 'Loop Entry',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
      },
    },
  } as StepConfig;
});

steps.set('loop_exit', (): StepConfig => {
  return {
    nodeConfig: {
      name: 'Loop Exit',
      group: 'flow_control',
      color: CATEGORY_COLORS.flow_control,
      icon: <FaArrowRightFromBracket />,
      source: false,
      target: true,
      multitarget: false,
      minInputs: 1,
      maxInstances: 1,
    },
    dataConfig: {
      getColumnsInfo: emptyColumnsInfo,
      jsonSchema: {
        title: 'Loop Exit',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              condition: {
                type: 'string',
                title: 'Exit Condition',
                description: 'Python expression to evaluate for loop termination (e.g., "done = len(input_df) < 10")',
              },
            },
            required: ['condition'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          condition: {
            'ui:widget': CodeEditorWidget,
            'ui:options': {
              language: 'python',
              initialValue: LOOP_EXIT_INITIAL_VALUE,
            },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('loop_workflow', (args = {}): StepConfig => {
  const workflows = args.workflows;
  const workflowsOptions = getWorkflowsOptions(workflows).filter((option) => option.const !== args.workflowId);
  const revisionsOptions = getRevisionsOptions(workflows);
  const maxRevision = Math.max(...(workflows ?? []).map((workflow) => workflow.revision));
  return {
    nodeConfig: {
      name: 'Loop Workflow',
      group: 'flow_control',
      color: CATEGORY_COLORS.flow_control,
      icon: <FaArrowRotateLeft />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
      definesExpectedColumns: true,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode, embeddedWorkflows?: Workflow[]) => {
        const prompts = args.prompts;
        const models = args.promptsModels;
        const files = args.files;
        return subworkflowColumnsInfo(data, embeddedWorkflows, prompts, models, files, workflows);
      },
      customForm: LoopWorkflow,
      jsonSchema: {
        title: 'Loop Workflow',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              workflow_id: {
                type: 'string',
                title: 'Workflow',
                anyOf: workflowsOptions,
              },
              revision: {
                type: 'number',
                title: 'Workflow revision',
                anyOf: Array.from({ length: maxRevision }, (_, i) => ({ const: i + 1 })).reverse(),
                readOnly: true,
              },
              max_iterations: {
                type: 'number',
                title: 'Maximum iterations',
                description: 'Maximum number of loop iterations',
                minimum: 1,
              },
            },
            required: ['workflow_id', 'revision'],
            dependencies: {
              workflow_id: {
                oneOf: revisionsOptions,
              },
            },
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          workflow_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': { autocomplete: true },
          },
        },
      },
    },
  } as StepConfig;
});

/* *** data preprocessing *** */

steps.set('code_parser', (): StepConfig => {
  return {
    nodeConfig: {
      name: 'Code Parser',
      group: 'parsers',
      color: CATEGORY_COLORS.parsers,
      icon: <HiOutlineCode />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns: [],
          outputColumns: [
            'file_name',
            'path',
            'unique_file_name',
            'ext',
            'include',
            'workspace',
            'project',
            'package',
            'method_id',
            'method_name',
            'parameters',
            'method_identifier',
            'row',
            'code',
            'dependencies_ids',
            'dependencies',
            'decorators',
            'decorators_name',
            'decorators_count',
          ],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Code parser',
        type: 'object',
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
        },
        required: ['name'],
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
      },
    },
  } as StepConfig;
});

steps.set('word_parser', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Docx/Rtf Parser',
      group: 'parsers',
      color: CATEGORY_COLORS.parsers,
      icon: <HiOutlineDocumentText />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as WordParserConfiguration;
        const inputColumns: string[] = [];
        if (cfg.column_name) {
          inputColumns.push(cfg.column_name);
        }
        if (cfg.extension) {
          inputColumns.push(cfg.extension);
        }

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns: [INDEX_COLUMN, 'type', 'chapter', 'content'],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Docx/Rtf Parser',
        type: 'object',
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Input "binary_content" (base64) column name',
                default: 'binary_content',
                format: CustomFormat.ColumnName,
              },
              extension: {
                type: 'string',
                title: 'Input "extension" column name',
                default: 'extension',
                format: CustomFormat.ColumnName,
              },
            },
            required: ['column_name', 'extension'],
          },
        },
        required: ['name'],
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          extension: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('embedding', (args = {}): StepConfig => {
  const promptsModelsOptions = getPromptsModelsOptions(args.promptsModels);

  return {
    nodeConfig: {
      name: 'Embeddings',
      group: 'legacy',
      color: CATEGORY_COLORS.legacy,
      icon: <FaTableCells />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as EmbeddingConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];
        const outputColumns = cfg.column_name ? [`embedding_${cfg.column_name}`] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Embeddings',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            properties: {
              model_id: {
                type: 'string',
                title: 'Prompts model',
                anyOf: promptsModelsOptions,
              },
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
              embedding_size: {
                type: 'number',
                title: 'Embedding size',
                default: 1536,
              },
              fake: {
                type: 'boolean',
                title: 'Fake embeddings',
                default: false,
              },
            },
            required: ['model_id', 'column_name'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
          model_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': {
              autocomplete: true,
              render: (modelId: string) => <ModelRenderer modelId={modelId} models={args.promptsModels!} />,
            },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('web_scraping', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Web scraping',
      group: 'advanced',
      color: CATEGORY_COLORS.advanced,
      icon: <FaGlobe />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as WebScrapingConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];
        const outputColumns = cfg.result_column ? [cfg.result_column, `${cfg.result_column}_content_type`] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Web scraping',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
              },
              result_column: {
                type: 'string',
                title: 'Result Column Name',
              },
              raw: {
                type: 'boolean',
                title: 'Raw data',
              },
            },
            required: ['column_name', 'result_column'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
        },
      },
    },
  } as StepConfig;
});

/* *** steps - data export *** */

steps.set('llm_extraction_validation', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Extraction Validation',
      group: 'validation',
      color: CATEGORY_COLORS.validation,
      icon: <ValidationIcon MainIcon={FaWandMagicSparkles} />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as LLMExtractionValidationConfiguration;
        const inputColumns: string[] = [];
        if (cfg.content_column_name) {
          inputColumns.push(cfg.content_column_name);
        }
        if (cfg.validation_target_column_name) {
          inputColumns.push(cfg.validation_target_column_name);
        }
        const outputColumns = cfg.result_column ? [cfg.result_column] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Extraction Validation',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              content_column_name: {
                type: 'string',
                title: 'Content Column Name',
                format: CustomFormat.ColumnName,
              },
              validation_target_column_name: {
                type: 'string',
                title: 'Validation Target Column Name',
                format: CustomFormat.ColumnName,
              },
              result_column: {
                type: 'string',
                title: 'Result Column Name',
                format: CustomFormat.ColumnName,
              },
            },
            required: ['content_column_name', 'validation_target_column_name', 'result_column'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          content_column_name: { 'ui:widget': ColumnFromFileWidget, 'ui:options': { columns: args.columns } },
          validation_target_column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('keyword_matching_validation', (args = {}): StepConfig => {
  const prompts = args.prompts ?? [];
  const promptsModelsOptions = getPromptsModelsOptions(args.promptsModels);
  const revisionsOptions = getPromptsRevisionsOptions(prompts);
  const maxRevision = Math.max(...(prompts ?? []).map((prompt) => prompt.revision));
  const filesOptions = getFilesOptions(args.files, JSON_FILE_TYPES);
  return {
    nodeConfig: {
      name: 'Keyword Matching Validation',
      group: 'validation',
      color: CATEGORY_COLORS.validation,
      icon: <ValidationIcon MainIcon={FaKey} />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as KeywordMatchingValidationConfiguration;
        const inputColumns: string[] = [];
        if (cfg.content_column_name) {
          inputColumns.push(cfg.content_column_name);
        }
        if (cfg.validation_target_column_name) {
          inputColumns.push(cfg.validation_target_column_name);
        }
        const outputColumns = cfg.result_column ? [cfg.result_column] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      customForm: LLMCommonForm,
      jsonSchema: {
        title: 'Keyword Matching Validation',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              model_id: {
                type: 'string',
                title: 'Prompts model',
                anyOf: promptsModelsOptions,
              },
              prompt_id: {
                type: ['string', 'null'],
                title: 'Prompt',
              },
              prompt_revision: {
                type: 'number',
                title: 'Prompt revision',
                anyOf: Array.from({ length: maxRevision }, (_, i) => ({ const: i + 1 })).reverse(),
                readOnly: true,
              },
              temperature: {
                type: ['number', 'null'],
                title: 'Temperature',
                default: 0,
              },
              content_column_name: {
                type: 'string',
                title: 'Content Column Name',
                format: CustomFormat.ColumnName,
              },
              validation_target_column_name: {
                type: 'string',
                title: 'Validation Target Column Name',
                format: CustomFormat.ColumnName,
              },
              result_column: {
                type: 'string',
                title: 'Result Column Name',
                format: CustomFormat.ColumnName,
              },
              keyword_category_definitions_file_path: {
                type: ['string', 'null'],
                title: 'Category definitions file',
                description: 'JSON file with keyword category definitions.',
                anyOf: [{ const: null }, ...filesOptions],
              },
              keyword_category_definitions: {
                type: 'object',
                title: 'Category definitions',
                description: 'Categories added here will be included on top of the ones from the file.',
                additionalProperties: {
                  type: 'string',
                },
              },
              log_prompts: {
                type: 'boolean',
                title: 'Log prompts',
                description: 'If enabled, the generated prompts will be logged for each row.',
                default: false,
              },
            },
            required: [
              'model_id',
              'prompt_id',
              'prompt_revision',
              'content_column_name',
              'validation_target_column_name',
              'result_column',
              'keyword_category_definitions',
            ],
            dependencies: {
              prompt_id: {
                oneOf: revisionsOptions,
              },
            },
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          'ui:order': ['*', 'log_prompts'],
          content_column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          validation_target_column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          keyword_category_definitions_file_path: {
            'ui:widget': FileManagementWidget,
            'ui:options': {
              accept: '.json',
            },
          },
          model_id: {
            'ui:widget': ListSelectionWidget,
            'ui:options': {
              autocomplete: true,
              render: (modelId: string) => <ModelRenderer modelId={modelId} models={args.promptsModels!} />,
            },
          },
          prompt_id: {
            'ui:widget': PromptWidget,
          },
        },
      },
    },
  } as StepConfig;
});

/* *** steps - data export *** */
steps.set('report_generate', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Generate Report',
      group: 'exporters',
      color: CATEGORY_COLORS.exporters,
      icon: <FaRegFileAlt />,
      source: true,
      target: true,
      multitarget: true,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => ({
        id: data.id,
        inputStepName: data.name,
        inputColumns: [],
        outputColumns: [INDEX_COLUMN, 'html', 'data'],
        removedColumns: [],
        replacesDataframe: true,
      }),
      jsonSchema: {
        title: 'Generate Report',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              source_files: {
                type: ['string', 'null'],
                title: 'Source step (files)',
              },
              source_methods: {
                type: ['string', 'null'],
                title: 'Source step (methods)',
              },
              source_folders: {
                type: ['string', 'null'],
                title: 'Source step (folders)',
              },
              source_data: {
                type: ['string', 'null'],
                title: 'Source step (data)',
              },
              export_format: {
                type: 'string',
                title: 'Export format',
                enum: ['html', 'markdown'],
                default: 'html',
              },
              yaml: {
                type: 'string',
                title: 'Yaml Configuration',
                default: REPORT_GENERATE_INITIAL_YAML,
              },
            },
            required: ['yaml', 'export_format'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          source_files: {
            'ui:widget': SourceStepWidget,
            'ui:options': {
              input_columns: args.columns,
              label: 'Source step (files)',
              required_fields: ['file_name', 'path'],
              text: 'Select a workflow step that outputs file metadata, including file name and path.\nRequired columns: ["file_name", "path"]',
            },
          },
          source_methods: {
            'ui:widget': SourceStepWidget,
            'ui:options': {
              input_columns: args.columns,
              label: 'Source step (methods)',
              required_fields: ['method_id', 'file_name', 'path'],
              text: 'Select a workflow step that outputs extracted methods (e.g. Code Parser).\nRequired columns: ["method_id", "method_name", "file_name", "path"]',
            },
          },
          source_folders: {
            'ui:widget': SourceStepWidget,
            'ui:options': {
              input_columns: args.columns,
              label: 'Source step (folders)',
              required_fields: ['folder_name', 'path'],
              text: 'Select a workflow step that outputs folder metadata, including folder name and path.\nRequired columns: ["folder_name", "path"]',
            },
          },
          source_data: {
            'ui:widget': SourceStepWidget,
            'ui:options': {
              input_columns: args.columns,
              label: 'Source step (data & pages)',
              required_fields: [],
              text: 'Select a workflow step that provides global data (single row).\nOptional columns: ["pages"] to dynamically define nested pages.\nExpected format: JSON array of PageDefinition.\nPageDefinition: {name: string, content: string, children?: PageDefinition[]}',
            },
          },
          export_format: {
            'ui:widget': ListSelectionWidget,
          },
          yaml: {
            'ui:widget': CodeEditorWidget,
            'ui:options': { language: 'yaml', initialValue: REPORT_GENERATE_INITIAL_YAML },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('data_export_file', (): StepConfig => {
  return {
    nodeConfig: {
      name: 'Export to file',
      group: 'exporters',
      color: CATEGORY_COLORS.exporters,
      icon: <FaFileExport />,
      source: false,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: emptyColumnsInfo,
      jsonSchema: {
        title: 'Export to file',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            properties: {
              file_type: {
                type: 'string',
                title: 'File type',
                enum: ['csv', 'xls'],
              },
            },
            required: ['file_type'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
      },
    },
  } as StepConfig;
});

steps.set('data_export_archive', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Export to archive',
      group: 'exporters',
      color: CATEGORY_COLORS.exporters,
      icon: <FaFileZipper />,
      source: false,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as DataExportArchiveConfiguration;
        const inputColumns: string[] = [];
        if (cfg.file_name_column) {
          inputColumns.push(cfg.file_name_column);
        }
        if (cfg.file_path_column) {
          inputColumns.push(cfg.file_path_column);
        }
        if (cfg.file_content_column) {
          inputColumns.push(cfg.file_content_column);
        }
        if (cfg.extension_column) {
          inputColumns.push(cfg.extension_column);
        }

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns: [],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Export to archive',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            properties: {
              file_name_column: {
                type: 'string',
                title: 'File name column',
                format: CustomFormat.ColumnName,
              },
              file_path_column: {
                type: ['string', 'null'],
                title: 'File path column',
                format: CustomFormat.ColumnName,
              },
              file_content_column: {
                type: 'string',
                title: 'File content column',
                format: CustomFormat.ColumnName,
              },
              extension_column: {
                type: ['string', 'null'],
                title: 'File extension column',
                format: CustomFormat.ColumnName,
              },
              binary: {
                type: 'boolean',
                title: 'Binary content?',
                default: false,
                description: 'Whether the file contents are in binary format.',
              },
            },
            required: ['file_name_column', 'file_content_column'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          file_name_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          file_path_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          file_content_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          extension_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('data_export_website', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Export to Developer Portal',
      group: 'exporters',
      color: CATEGORY_COLORS.exporters,
      icon: <FaFileCode />,
      source: false,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as DataExportWebsiteConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns: [],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Export to Developer Portal',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            properties: {
              column_name: {
                type: 'string',
                title: 'HTML content column',
                format: CustomFormat.ColumnName,
              },
              file_name: {
                type: 'string',
                title: 'File name',
                description: 'Name of the exported HTML file',
              },
            },
            required: ['column_name'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('visualize_diagram', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Visualize Mermaid diagram',
      group: 'advanced',
      color: CATEGORY_COLORS.advanced,
      icon: <FaDiagramProject />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as VisualizeDiagramConfiguration;
        const inputColumns = cfg.column_name ? [cfg.column_name] : [];
        const outputColumns = cfg.export_file_column ? [cfg.export_file_column] : [];

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns,
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Visualize Mermaid diagram',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            type: 'object',
            properties: {
              column_name: {
                type: 'string',
                title: 'Column Name',
                format: CustomFormat.ColumnName,
              },
              image_type: {
                type: 'string',
                title: 'Image type',
                enum: ['svg', 'png'],
                default: 'svg',
              },
              export_file_column: {
                type: ['string', 'null'],
                title: 'Export file column name',
                description: 'If set, stores the exported image file ID in the specified column.',
                format: CustomFormat.ColumnName,
              },
              export_rows_separately: {
                type: 'boolean',
                title: 'Export rows separately',
                description:
                  'If enabled, exports each row as a separate image file. Otherwise exports an archive containing all images.',
                default: false,
              },
            },
            required: ['column_name', 'image_type'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          column_name: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('github_wiki_page_upload', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'GitHub Wiki page upload',
      group: 'git',
      color: CATEGORY_COLORS.git,
      icon: <FaGithub />,
      source: true,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as GitHubWikiPageUploadConfiguration;
        const inputColumns: string[] = [];
        if (cfg.page_name_column) {
          inputColumns.push(cfg.page_name_column);
        }
        if (cfg.page_content_column) {
          inputColumns.push(cfg.page_content_column);
        }

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns: [],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'GitHub wiki page upload',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            properties: {
              repo_url: {
                type: 'string',
                title: 'Repository URL',
                pattern: '^https://github\\.com/[^/]+/[^/]+$',
              },
              page_name_column: {
                type: 'string',
                title: 'Page name column',
                format: CustomFormat.ColumnName,
              },
              page_content_column: {
                type: 'string',
                title: 'Page content column',
                format: CustomFormat.ColumnName,
              },
              secret_id: {
                type: 'string',
                title: 'Authentication Secret',
                description: 'GitHub access token for authentication.',
              },
              git_user_name: {
                type: ['string', 'null'],
                title: 'Git user name',
                description: 'Git user name to use for the commit.',
              },
              git_user_email: {
                type: ['string', 'null'],
                title: 'Git user email',
                description: 'Git user email to use for the commit.',
                format: 'email',
              },
            },
            required: ['repo_url', 'page_name_column', 'page_content_column', 'secret_id'],
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          page_name_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          page_content_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          secret_id: {
            'ui:widget': SecretWidget,
            'ui:options': {
              types: ['git_auth'],
            },
          },
        },
      },
    },
  } as StepConfig;
});

steps.set('git_export', (args = {}): StepConfig => {
  return {
    nodeConfig: {
      name: 'Git Export',
      group: 'git',
      color: CATEGORY_COLORS.git,
      icon: <FaGitAlt />,
      source: false,
      target: true,
      multitarget: false,
      minInputs: 1,
    },
    dataConfig: {
      getColumnsInfo: (data: WorkflowNode) => {
        const cfg = data.configuration as GitExportConfiguration;
        const inputColumns: string[] = [];
        if (cfg.file_name_column) {
          inputColumns.push(cfg.file_name_column);
        }
        if (cfg.file_path_column) {
          inputColumns.push(cfg.file_path_column);
        }
        if (cfg.file_content_column) {
          inputColumns.push(cfg.file_content_column);
        }

        return {
          id: data.id,
          inputStepName: data.name,
          inputColumns,
          outputColumns: [],
          removedColumns: [],
          replacesDataframe: false,
        };
      },
      jsonSchema: {
        title: 'Git Export',
        type: 'object',
        required: ['name'],
        properties: {
          name: { type: 'string', title: 'Name' },
          description: { type: 'string', title: 'Description' },
          configuration: {
            title: 'Configuration',
            properties: {
              repo_url: {
                type: 'string',
                title: 'Repository URL',
              },
              branch: {
                type: ['string', 'null'],
                title: 'Branch name',
                description:
                  "Branch to push changes to (will be created if doesn't exist). Defaults to the default branch of the repository.",
              },
              generate_unique_branch: {
                type: 'boolean',
                title: 'Generate unique branch name?',
                description:
                  'If enabled, a unique branch name will be generated for each execution by appending a random suffix to the branch name.',
                default: false,
              },
              base_branch: {
                type: ['string', 'null'],
                title: 'Base branch',
                description:
                  'Base branch for creating a new branch from. Defaults to the default branch of the repository.',
              },
              generate_commit_message: {
                type: 'boolean',
                title: 'Generate commit message using LLM',
                description: 'If selected, commit message is generated using LLM',
                default: true,
              },
              secret_id: {
                type: 'string',
                title: 'Authentication Secret',
                description: 'Authentication secret for private repositories e.g. access token or username:password',
              },
              file_name_column: {
                type: 'string',
                title: 'File name column',
                format: CustomFormat.ColumnName,
                description: 'Column containing the file names.',
              },
              file_path_column: {
                type: 'string',
                title: 'File path column',
                format: CustomFormat.ColumnName,
                description: 'Column containing the file paths.',
              },
              file_content_column: {
                type: 'string',
                title: 'File content column',
                format: CustomFormat.ColumnName,
                description: 'Column containing the file contents.',
              },
              binary: {
                type: 'boolean',
                title: 'Binary content?',
                default: false,
                description: 'Whether the file contents are in binary format.',
              },
              git_user_name: {
                type: ['string', 'null'],
                title: 'Git user name',
                description: 'Git user name to use for the commit.',
              },
              git_user_email: {
                type: ['string', 'null'],
                title: 'Git user email',
                description: 'Git user email to use for the commit.',
                format: 'email',
              },
            },
            required: ['repo_url', 'file_name_column', 'file_path_column', 'file_content_column', 'secret_id'],
            dependencies: {
              generate_commit_message: {
                oneOf: [
                  {
                    properties: {
                      generate_commit_message: {
                        enum: [true],
                      },
                    },
                  },
                  {
                    properties: {
                      generate_commit_message: {
                        enum: [false],
                      },
                      commit_message: {
                        type: ['string', 'null'],
                        title: 'Commit message',
                        description: 'Message for the commit. Defaults to "Update files".',
                      },
                    },
                  },
                ],
              },
            },
          },
        },
      },
      uiSchema: {
        description: {
          'ui:autocomplete': 'off',
        },
        configuration: {
          'ui:order': [
            'repo_url',
            'branch',
            'generate_unique_branch',
            'base_branch',
            'generate_commit_message',
            'commit_message',
            'secret_id',
            'file_name_column',
            'file_path_column',
            'file_content_column',
            'binary',
            'git_user_name',
            'git_user_email',
          ],
          file_name_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          file_path_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          file_content_column: {
            'ui:widget': ColumnFromFileWidget,
            'ui:options': { columns: args.columns },
          },
          secret_id: {
            'ui:widget': SecretWidget,
            'ui:options': {
              types: ['git_auth'],
            },
          },
        },
      },
    },
  } as StepConfig;
});

/* *** helpers functions *** */

type SelectOption = {
  const: string | undefined | null;
  title: string;
};

function getPromptsModelsOptions(promptsModels?: PromptModel[], provider?: ModelType): SelectOption[] {
  return (promptsModels ?? [])
    .filter((model) => (provider ? model.type === provider : true))
    .map((model) => ({
      const: model.id,
      title: model.name,
    }));
}

export function getFilesOptions(files?: FileManagement[], allowedMime?: string[]): SelectOption[] {
  const filesOptions: SelectOption[] = [];
  let filesList = files ?? [];
  if (allowedMime) {
    filesList = filesList.filter((file) => allowedMime.includes(simplifiedMimeType(file.mime_type)));
  }
  for (const file of filesList) {
    filesOptions.push({
      const: innerDownloadLink(file.id),
      title: file.file_name,
    });
  }

  return filesOptions;
}

function getWorkflowsOptions(workflowModels?: Workflow[]): SelectOption[] {
  const workflowOptions: SelectOption[] = [];
  for (const model of workflowModels ?? []) {
    workflowOptions.push({
      const: model.id,
      title: model.name,
    });
  }

  return workflowOptions;
}

type RevisionOption = {
  properties: {
    workflow_id: {
      anyOf: { const: string; title: string }[];
    };
    revision: {
      anyOf: { const: number }[];
      type: 'number';
      title: string;
      readOnly: boolean;
      default: number;
    };
  };
};

type PromptRevisionOption = {
  properties: {
    prompt_id: {
      anyOf: { const: string; title: string }[];
    };
    prompt_revision: {
      anyOf: { const: number }[];
      type: 'number';
      title: string;
      readOnly: boolean;
      default: number;
    };
    input_data?: {
      type: 'object';
      title: string;
      properties: Record<string, { type: string }>;
      required: string[];
    };
  };
};

type EvaluationPromptRevisionOption = {
  properties: {
    evaluation_prompt_id: {
      anyOf: { const: string; title: string }[];
    };
    evaluation_prompt_revision: {
      anyOf: { const: number }[];
      type: 'number';
      title: string;
      readOnly: boolean;
      default: number;
    };
  };
};

function getRevisionsOptions(workflows?: Workflow[]): RevisionOption[] {
  const results: RevisionOption[] = [];
  for (const workflow of workflows ?? []) {
    results.push({
      properties: {
        workflow_id: {
          anyOf: [{ const: workflow.id, title: workflow.name }],
        },
        revision: {
          anyOf: Array.from({ length: workflow.revision }, (_, i) => ({ const: i + 1 })).reverse(),
          type: 'number',
          title: 'Workflow revision',
          readOnly: false,
          default: workflow.revision,
        },
      },
    });
  }

  return results;
}

function getPromptsRevisionsOptions(
  prompts?: Prompt[],
  includeInputData = false,
  fixedInputs?: string[],
): PromptRevisionOption[] {
  const results: PromptRevisionOption[] = [];
  for (const prompt of prompts ?? []) {
    const properties: PromptRevisionOption['properties'] = {
      prompt_id: {
        anyOf: [{ const: prompt.id, title: prompt.prompt_name }],
      },
      prompt_revision: {
        anyOf: Array.from({ length: prompt.revision }, (_, i) => ({ const: i + 1 })).reverse(),
        type: 'number',
        title: 'Prompt revision',
        readOnly: false,
        default: prompt.revision,
      },
    };

    if (includeInputData) {
      const filteredInputs = prompt.inputs.filter((input) => !fixedInputs?.includes(input));
      properties.input_data = {
        type: 'object',
        title: 'Prompt inputs',
        properties: filteredInputs.reduce((a, v) => ({ ...a, [v]: { type: 'string' } }), {}),
        required: filteredInputs,
      };
    }

    results.push({ properties });
  }

  return results;
}

function getEvaluationPromptsRevisionsOptions(prompts?: Prompt[]): EvaluationPromptRevisionOption[] {
  return (
    prompts?.map((prompt) => ({
      properties: {
        evaluation_prompt_id: {
          anyOf: [{ const: prompt.id, title: prompt.prompt_name }],
        },
        evaluation_prompt_revision: {
          anyOf: Array.from({ length: prompt.revision }, (_, i) => ({ const: i + 1 })).reverse(),
          type: 'number',
          title: 'Evaluation prompt revision',
          readOnly: false,
          default: prompt.revision,
        },
      },
    })) ?? []
  );
}

type FunctionRevisionOption = {
  properties: {
    function_id: {
      anyOf: { const: string; title: string }[];
    };
    function_revision: {
      anyOf: { const: number }[];
      type: 'number';
      title: string;
      readOnly: boolean;
      default: number;
    };
  };
};

function getFunctionRevisionsOptions(functions?: PythonFunctionSummary[]): FunctionRevisionOption[] {
  return (
    functions?.map((func) => ({
      properties: {
        function_id: {
          anyOf: [{ const: func.id, title: func.name }],
        },
        function_revision: {
          anyOf: Array.from({ length: func.revision }, (_, i) => ({ const: i + 1 })).reverse(),
          type: 'number',
          title: 'Function revision',
          readOnly: false,
          default: func.revision,
        },
      },
    })) ?? []
  );
}

function getSecretsOptions(secrets?: Secret[], type?: SecretType): SelectOption[] {
  return (secrets ?? [])
    .filter((secret) => !type || secret.type === type)
    .map((secret) => ({
      const: secret.id,
      title: `${secret.name} - ${secret.value.length > 24 ? `${secret.value.slice(0, 12)}${secret.value.slice(-12)}` : secret.value}`,
    }));
}

function getProjectsOptions(projects?: Project[]): SelectOption[] {
  return (projects ?? []).map((project) => ({
    const: project.id,
    title: project.name,
  }));
}

/* *** export *** */

function getFormSchema(stepType: WorkflowNodeType, args: StepConfigArgs = {}): JSONSchema7 | undefined {
  return getStepConfig(stepType, args)?.dataConfig.jsonSchema;
}

function getUiSchema(
  stepType: WorkflowNodeType,
  prompts?: Prompt[],
  filesColumns?: NodeColumnsInfo[],
  promptsModels?: PromptModel[],
): UiSchema | undefined {
  const config = getStepConfig(stepType, { prompts, promptsModels, columns: filesColumns });
  return config?.dataConfig.uiSchema;
}

function emptyColumnsInfo(node: WorkflowNode): NodeColumnsInfo {
  return {
    id: node.id,
    inputStepName: node.name,
    stepType: node.type,
    inputColumns: [],
    outputColumns: [],
    removedColumns: [],
    replacesDataframe: false,
  };
}

async function subworkflowColumnsInfo(
  data: WorkflowNode,
  embeddedWorkflows?: Workflow[],
  prompts?: Prompt[],
  models?: PromptModel[],
  files?: FileManagement[],
  workflows?: Workflow[],
): Promise<NodeColumnsInfo> {
  const cfg = data.configuration as SubworkflowCommonConfiguration;

  const workflow = embeddedWorkflows?.find(
    (workflow) => workflow.id == cfg.workflow_id && workflow.revision == cfg.revision,
  );
  if (!workflow) {
    return emptyColumnsInfo(data);
  }

  const columnsSet = new Set<string>();
  for (const node of workflow.nodes) {
    const columns = await getNodeColumnsInfo(node, prompts, models, files, workflows, embeddedWorkflows, workflow.id);
    columns.outputColumns.forEach((col) => columnsSet.add(col));
  }

  return {
    id: data.id,
    inputStepName: data.name,
    fileName: `${workflow.name} [${workflow.revision}]`,
    inputColumns: [],
    outputColumns: Array.from(columnsSet),
    removedColumns: [],
    replacesDataframe: false,
  };
}

function getStepConfig(stepType: WorkflowNodeType, args: StepConfigArgs = {}): StepConfig | undefined {
  return steps.get(stepType)?.(args);
}

async function getNodeColumnsInfo(
  data: WorkflowNode,
  prompts?: Prompt[],
  promptsModels?: PromptModel[],
  files?: FileManagement[],
  workflows?: Workflow[],
  embeddedWorkflows?: Workflow[],
  workflowId?: string,
  secrets?: Secret[],
): Promise<NodeColumnsInfo> {
  const stepConfig = getStepConfig(data.type, { prompts, promptsModels, files, workflows, workflowId, secrets });
  const dataConfig = stepConfig?.dataConfig;
  if (!dataConfig) return emptyColumnsInfo(data);

  const expectedColumns = data.expected_columns;

  const info = await dataConfig.getColumnsInfo(data, embeddedWorkflows);
  info.stepType = data.type;
  if (!expectedColumns) {
    return info;
  }

  info.outputColumns = [...new Set([...info.outputColumns, ...expectedColumns.output_columns])];
  info.inputColumns = [...new Set([...info.inputColumns, ...expectedColumns.input_columns])];
  info.removedColumns = [...new Set([...info.removedColumns, ...expectedColumns.removed_columns])];
  info.replacesDataframe = expectedColumns.replaces_dataframe ?? info.replacesDataframe;

  return info;
}

function getNodeConfig(stepType: WorkflowNodeType): NodeConfig | undefined {
  const configFn = steps.get(stepType);
  if (configFn) {
    return configFn().nodeConfig;
  } else {
    return undefined;
  }
}

const getAllNodeConfigs = (): Map<WorkflowNodeType, NodeConfig> => {
  return new Map([...steps.entries()].map(([stepType, configFn]) => [stepType, configFn().nodeConfig]));
};

const getNodeFormComponent = (stepType: WorkflowNodeType) => {
  const configFn = steps.get(stepType);
  return configFn?.().dataConfig.customForm ?? FormBlock;
};

const hasCustomForm = (stepType: WorkflowNodeType): boolean => {
  const configFn = steps.get(stepType);
  return !!configFn?.().dataConfig.customForm;
};

function getAllNodesKeys(): WorkflowNodeType[] {
  return Array.from(steps.keys());
}

function getAllNodeConfigsWithKeys(): WorkflowNodeConfigWithKey[] {
  const allNodesConfig = getAllNodeConfigs();
  return Array.from(allNodesConfig.entries()).map(([nodeKey, nodeConfig]) => ({
    ...nodeConfig,
    nodeKey,
  }));
}

const specificNodeTypes = {
  embed_workflow: EmbedWorkflowNode,
  merge: MergeNode,
  llm_prompt: LLMNodeWithInputData,
  llm_map_reduce: LLMNodeWithInputData,
  data_loader_llm_research: LLMNodeWithInputData,
  llm_research: LLMNodeWithInputData,
  proxy: ProxyNode,
  python_function: PythonFunctionNode,
};

export {
  getFormSchema,
  getUiSchema,
  getNodeConfig,
  getNodeColumnsInfo,
  getAllNodesKeys,
  getNodeFormComponent,
  getAllNodeConfigs,
  getSecretsOptions,
  getProjectsOptions,
  getAllNodeConfigsWithKeys,
  hasCustomForm,
  specificNodeTypes,
  schemaLLMExtractionSchemaProperty,
  uiSchemaLLMExtractionSchemaProperty,
  uiSchemaLLMClassifySchemaProperty,
  schemaLLMSummarizeSchemaProperty,
  schemaLLMClassifySchemaProperty,
  CATEGORY_COLORS,
  getCategoryColors,
  getNodeColorByGroup,
};
